/**
 * Quizzy React JS QuizExam page
 * Property of Quizzy (2023)
 *
 * This source code is the property of Quizzy. Unauthorized use,
 * reproduction, or distribution of this code, or any portion of it,
 * is strictly prohibited without prior written permission from Quizzy.
 *
 * Quizzy. reserves the right to take legal action against any
 * individual or entity found to be in violation of these terms.
 */

import React, { useEffect, useState } from "react";
import ProgressBar from "@ramonak/react-progress-bar";
import { CiStopwatch } from "react-icons/ci";
import { TbExclamationMark } from "react-icons/tb";
import ReactGA from "react-ga4";
import {
  BsArrowLeftShort,
  BsArrowRightShort,
  BsCheck,
  BsExclamationCircle,
  BsInfoCircle,
} from "react-icons/bs";
import "./QuizExam.scss";
import {
  begin_assigned_quiz,
  create_practice_exam,
  fetch_quiz_info,
  get_quiz_details,
  get_quiz_info,
  get_session_token,
  get_set_data,
  get_tutorial_progress,
  get_user,
  override_saq,
  overwrite_quiz_answers,
  retake_quiz,
  submit_quiz,
  submit_quiz_answer,
  submit_quiz_question,
} from "../../network/communication";
import { useNavigate, useParams } from "react-router-dom";
import Xarrow, { Xwrapper } from "react-xarrows";
import { addStyles, EditableMathField, StaticMathField } from "react-mathquill";
import { useRef } from "react";
import Popup from "../../components/Popup/Popup";
import DoughnutChart from "../../components/DoughnutChart/DoughnutChart";
import { SteppedLineChart } from "../../components/SteppedLineChart/SteppedLineChart";

import AnalyticsBadge from "../../components/AnalyticsBadge/AnalyticsBadge";
import AnalyticsBanner from "../../components/AnalyticsBanner/AnalyticsBanner";

import QuizzyPlusBanner from "../../components/QuizzyPlusBanner/QuizzyPlusBanner";
import Tooltip from "../../components/Tooltip/Tooltip";
import WhiteButton from "../../components/WhiteButton/WhiteButton";
import PlusIcon from "../../components/PlusIcon/PlusIcon";
import { useSpeechSynthesis } from "react-speech-kit";

import ShareQuizScore from "../../components/ShareQuizScore/ShareQuizScore";
import CTAButton from "../../components/CTAButton/CTAButton";
import FoldedPopup from "../../components/FoldedPopup/FoldedPopup";
import QuizExamPlaceholders from "../../components/QuizExamPlaceholders/QuizExamPlaceholders";
import QuizTutorial from "../../components/Tutorials/QuizTutorial.jsx";
import SampleData from "../../components/SampleData/SampleData.jsx";
import QuizzyPlusTutorial from "../../components/Tutorials/QuizzyPlusTutorial.jsx";

addStyles();

const QuizExam = (props) => {
  // State variables
  // Match choices states
  const [matchChoice1, setMatchChoice1] = useState(null);
  const [matches, setMatches] = useState({}); // matcha0, matcha2
  const [selectedMatchOption, setSelectedMatchOption] = useState(null);
  const [quizScheduledPopupEnabled, setQuizScheduledPopupEnabled] =
    useState(false);

  const { speak, cancel, voices } = useSpeechSynthesis();

  const user = props.user;
  const setUser = props.setUser;

  const [quizTutorialEnabled, setQuizTutorialEnabled] = useState(false);
  const [quizTutorialPhase, setQuizTutorialPhase] = useState(0);
  const [quizTutorialStepIndex, setQuizTutorialStepIndex] = useState(0);

  const [quizzyPlusQuizTutorialEnabled, setQuizzyPlusQuizTutorialEnabled] =
    useState(false);
  const [quizzyPlusQuizTutorialPhase, setQuizzyPlusQuizTutorialPhase] =
    useState(0);
  const [quizzyPlusQuizTutorialStepIndex, setQuizzyPlusQuizTutorialStepIndex] =
    useState(0);

  const [isAwaitingStart, setIsAwaitingStart] = useState(false);
  useEffect(() => {
    get_user(function (user) {
      if (user && user.success === true) {
        setUser(user);
      } else {
        navigate("/login");
      }
    });
  }, []);

  // Constants
  const QUIZ_RESULTS_MAXIMUM_XARROW_RESULTS = 7;

  const COLORS = [
    "#E85871",
    "#8358E8",
    "#58AEE8",
    "#58E8D4",
    "#58E866",
    "#E8E858",
    "#E88458",
    "#5883E8",
    "#58E8E3 ",
  ];
  const alphabet = [
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H",
    "I",
    "J",
    "K",
    "L",
    "M",
    "N",
    "O",
    "P",
    "Q",
    "R",
    "S",
    "T",
    "U",
    "V",
    "W",
    "X",
    "Y",
    "Z",
  ];

  // Params and navigation
  const { setid, title, id } = useParams("");
  const navigate = useNavigate();

  // Viewing states
  const [isOnMainViewingResultsPage, setIsOnMainViewingResultsPage] =
    useState(false);
  const [isViewingDetails, setIsViewingDetails] = useState(false);
  const [viewingResults, setViewingResults] = useState(id ? true : false);
  const [quizView, setquizView] = useState(false);
  const [createQuizButtonEnabled, setCreateQuizButtonEnabled] = useState(true);
  // Quiz states
  const [preparedAnswerForSubmission, setPreparedAnswerForSubmission] =
    useState(null);
  const [nQuestions, setNQuestions] = useState(null);
  const [quizDetails, setQuizDetails] = useState(null);
  const [resultsInfo, setResultsInfo] = useState(null);
  const [quizId, setQuizId] = useState(null);
  const [optionsResponse, setOptionsResponse] = useState({});
  const [quizQueue, setQuizQueue] = useState([]);
  const [shareQuizScoreEnabled, setShareQuizScoreEnabled] = useState(false);
  const [isViewingCorrectAnswersMatching, setIsViewingCorrectAnswersMatching] =
    useState(false);

  // Time and date functions
  const dateWithTimeZone = (
    timeZone,
    year,
    month,
    day,
    hour,
    minute,
    second
  ) => {
    let date = new Date(Date.UTC(year, month, day, hour, minute, second));

    let utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
    let tzDate = new Date(date.toLocaleString("en-US", { timeZone: timeZone }));
    let offset = utcDate.getTime() - tzDate.getTime();

    date.setTime(date.getTime() + offset);

    return date;
  };

  // Autosave and answer states
  const [lastAutosaveTime, setLastAutosaveTime] = useState(Date.now());
  const [lastAnswerChanged, setLastAnswerChanged] = useState(null);

  // Quiz interaction states
  const [isTakingQuiz, setIsTakingQuiz] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [matchingAnswer, setMatchingAnswer] = useState("");

  // Set data and time-related states
  const [setData, setSetData] = useState();
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [progress, setProgress] = useState(0);

  // Popup states
  const [submitQuizPopup, setSubmitQuizPopup] = useState(false);

  // Deadline and settings states
  const [deadline, setDeadline] = useState(""); //"December, 5, 2022 21:00:00";
  const [settingsVisible, setSettingsVisible] = useState(false);

  // Refs
  const audioRadio = useRef(null);
  const answerWithRadio = useRef(null);
  const typesofQuestionsCheckboxes = useRef(null);
  const typesOfTermsRadio = useRef(null);
  const imagesRadio = useRef(null);
  const nQuestionsForm = useRef(null);
  const timeSelection = useRef(null);
  const emailForm = useRef(null);

  const [mdtsChecked, setMDTsChecked] = useState(false);
  const [submittedQuiz, setSubmittedQuiz] = useState(false);

  var quizProgress = {
    dashTitle: " Quizzy's Smart Projection",
    dashData: [],
    fillTitle: " Your Quiz Progress",
    fillData: [],
    labels: [],
    dotColor: "#58E892",
    hideXTicks: true,
    yTicksCallback: function (v) {
      return v + "%";
    },
  };

  function disableQuizInputs() {
    const container = document.querySelector(".quiz-exam-container");
    const inputs = container.getElementsByTagName("input");
    container.style.opacity = 0.5;
    for (let i = 0; i < inputs.length; i++) {
      inputs[i].disabled = true;
    }
  }

  function secondsToHms(d) {
    d = Number(d);
    var h = Math.floor(d / 3600);
    var m = Math.floor((d % 3600) / 60);
    var s = Math.floor((d % 3600) % 60);

    var hDisplay = h > 0 ? h + (h == 1 ? " hour " : " hours ") : "";
    var mDisplay = m > 0 ? m + (m == 1 ? " mins " : " mins ") : "";
    var sDisplay = s > 0 ? s + (s == 1 ? " secs" : " secs") : "";

    if (mDisplay != "") {
      hDisplay = h > 0 ? h + "hrs " : "";
      mDisplay = m > 0 ? m + "m " : "";
      if (s > 0) {
        sDisplay = s + "s";
      }
    }
    return hDisplay + mDisplay + sDisplay;
  }

  var timeProgress = {
    fillTitle: " Your Quiz Time",
    fillData: [],
    labels: [],
    dotColor: "#8358E8",
    hideXTicks: true,
    noPercent: true,
    yTicksCallback: function (v) {
      return secondsToHms(v);
    },
  };

  var highestMissedTermIndex = 0;
  if (quizDetails != null) {
    var detailCount = 0;
    for (var key in quizDetails[0]) {
      detailCount++;
      quizProgress.labels.push("Attempt #" + detailCount);
      quizProgress.fillData.push(quizDetails[0][key][0]);
      quizProgress.dashData.push(quizDetails[0][key][0]);
    }
    for (var key in quizDetails[1]) {
      detailCount++;
      quizProgress.labels.push("Attempt #" + detailCount);
      quizProgress.dashData.push(quizDetails[1][key]);
    }
    quizProgress.dotColor = getGradeColor(
      quizDetails[0][quizDetails[0].length - 1][0]
    );
    // time prog
    var detailCount = 0;
    for (var key in quizDetails[3]) {
      detailCount++;
      timeProgress.labels.push("Attempt #" + detailCount);
      timeProgress.fillData.push(quizDetails[3][key]);
    }
    // most missed terms - calculate highest missed term index
    for (var key in quizDetails[2]) {
      if (quizDetails[2][key] > highestMissedTermIndex) {
        highestMissedTermIndex = quizDetails[2][key];
      }
    }
  }

  const parts = [
    "Part I - Multiple Choice",
    "Part II - True/False",
    "Part III - Short Answer Questions",
    "Part IV - Matching",
  ];

  const getPart = function () {
    var count = 0;
    var part = "";
    Object.keys(quizQueue).forEach((key) => {
      quizQueue[key].forEach((questionData, index) => {
        if (count == currentQuestion) {
          part = parts[key];
        }
        count++;
      });
    });
    return part;
  };

  const getCurrentQuestionIndex = function () {
    var count = 0;
    var idx = 0;
    Object.keys(quizQueue).forEach((key) => {
      quizQueue[key].forEach((questionData, index) => {
        if (count == currentQuestion) {
          idx = index;
        }
        count++;
      });
    });
    return idx;
  };

  const getColor = function (i) {
    return COLORS[i % (COLORS.length - 1)];
  };

  const getQuestionDataByIndex = function (i) {
    var qd = {
      type: "short-answer",
      options: [],
    };
    var questionCount = 0;
    Object.keys(quizQueue).forEach((key) => {
      quizQueue[key].forEach((questionData, index) => {
        if (questionCount == i) {
          qd = questionData;
        }
        questionCount++;
      });
    });

    return qd;
  };

  const getQuestionDataByIndex_Results = function (i) {
    var qd;
    var results = resultsInfo?.grade[3];
    var questionCount = 0;

    if (!results) {
      return {
        type: "short-answer",
        options: [],
      };
    }
    Object.keys(results).forEach((key) => {
      results[key].forEach((questionData, index) => {
        if (questionCount == i) {
          qd = questionData;
        }
        questionCount++;
      });
    });

    return qd;
  };

  const overrideCurrentQuestionAnswer = function (answer) {
    // deprecated; used when it was client sided
    var queue = [...quizQueue];
    var didSomething = false;
    var questionCount = 0;
    queue.forEach((data, k) => {
      data.forEach((questionData, index) => {
        if (questionCount == currentQuestion && !didSomething) {
          queue[k][index].answer = answer;
          didSomething = true;
          return;
        }
        questionCount++;
      });
    });
    setQuizQueue(queue);
    setLastAnswerChanged(new Date());
  };

  const getCurrentQuestionData = function () {
    return getQuestionDataByIndex(currentQuestion);
  };

  const updateMatchDataForCurrentQuestion = () => {
    var ans = getCurrentQuestionData()?.answer;
    if (getCurrentQuestionData().type == "matching") {
      var mat = {};
      Object.keys(ans)?.forEach((k, index) => {
        //ans.forEach((v, index) => {
        var v = ans[parseInt(k)];
        if (v != undefined) {
          mat["matcha" + k] = ["matchb" + v, v];
        }
      });
      setMatches(mat);
    } else {
      setMatches({});
      setMatchChoice1(null);
    }
  };

  const updateMatchDataForCorrectAnswers = () => {
    console.log("updating for correct");
    if (getCurrentQuestionData().type === "matching" && viewingResults) {
      var mat = {};
      console.log(getCurrentQuestionData());
      getCurrentQuestionData()?.options[0]?.forEach((k, index) => {
        mat["matcha" + k] = ["matchb" + k, k];
      });
      console.log(mat);
      setMatches(mat);
    }
  };

  const isValidMatchChoice = function (i, ans) {
    const values = Object.values(ans);
    const occurrences = {};

    for (let j = 0; j < values.length; j++) {
      const v = values[j];
      occurrences[v] = occurrences[v] ? occurrences[v] + 1 : 1;

      if (occurrences[v] > 1) {
        return false;
      }
    }

    return true;
  };

  const getProgress = function () {
    let progress = 0;
    let questionCount = 0;

    Object.values(quizQueue).forEach((queue) => {
      if (queue !== quizQueue[3]) {
        queue.forEach((questionData) => {
          const { answer } = questionData;

          if (
            answer &&
            (Array.isArray(answer) || typeof answer === "string") &&
            answer.length > 0
          ) {
            progress++;
          } else if ((answer && !Array.isArray(answer)) || answer == 0) {
            progress++;
          }

          questionCount++;
        });
      }
    });

    if (quizQueue[3]?.length > 0) {
      // matching
      Object.values(quizQueue[3][0].answer).forEach(() => {
        progress++;
      });
      questionCount += Object.keys(quizQueue[3][0].options[0]).length;
    }

    const percentage = Math.ceil((progress / questionCount) * 100);

    return [percentage, questionCount];
  };

  const arrayOfQuestions = function () {
    var questionCount = 0;
    var questions = [];
    Object.keys(quizQueue).forEach((key, k) => {
      quizQueue[key].forEach((questionData, index) => {
        if (k != 3) {
          questions[questionCount] = questionCount;
          questionCount++;
        }
      });
    });

    return questions;
  };

  const getOriginalMatcher = function (v) {
    if (
      getCurrentQuestionData() == null ||
      getCurrentQuestionData()?.answer == null
    ) {
      return null;
    }
    var ind = null;
    Object.keys(getCurrentQuestionData()?.answer)?.forEach((k, index) => {
      if (v == getCurrentQuestionData()?.answer[parseInt(k)]) {
        ind = k;
      }
    });

    return ind;
  };

  const getMatchingLineColor = (index) => {
    if (!viewingResults) {
      return getColor(getOriginalMatcher(matches[index]?.[1]));
    } else {
      if (
        resultsInfo?.grade?.[3]?.[3]?.[0]?.[
          getOriginalMatcher(matches[index]?.[1])
        ] === "C" ||
        isViewingCorrectAnswersMatching
      ) {
        return "#58E892";
      }
    }
    return "#D83C3C";
  };
  const getTime = () => {
    if (deadline != null) {
      const time = Date.parse(deadline) - Date.now();
      if (time <= 0 && !viewingResults && isTakingQuiz) {
        // ran out of time buddy
        if (!submittedQuiz) {
          updateQuizDB((d) => {
            submit_quiz(setid, (data) => {
              setSubmittedQuiz(true);
              if (data["success"]) {
                navigate("./" + d["id"] + "/results");
                window.location.reload();
              }
            });
          });
        }
      }
      setDays(Math.floor(time / (1000 * 60 * 60 * 24)));
      setHours(Math.floor((time / (1000 * 60 * 60)) % 24));
      setMinutes(Math.floor((time / 1000 / 60) % 60));
      setSeconds(Math.floor((time / 1000) % 60));
    }
  };

  const isTouchDevice =
    "maxTouchPoints" in navigator &&
    navigator.maxTouchPoints > 0 &&
    /(iPhone|iPad|Android|webOS|BlackBerry|Windows Phone|Opera Mini|IEMobile)/i.test(
      navigator.userAgent
    );

  const getTimerString = () => {
    if (viewingResults) {
      disableQuizInputs();
      return "Finished Quiz";
    }

    let s = "";

    if (days > 0) {
      s += days.toString().padStart(2, "0") + ":";
    }

    s += hours.toString().padStart(2, "0") + ":";
    s += minutes.toString().padStart(2, "0") + ":";
    s += seconds.toString().padStart(2, "0");

    if (seconds < 0) {
      s = "Time's over!";
    } else {
      if (!isTouchDevice) {
        s += " remaining";
      }
    }

    return s;
  };

  function checkIfAllMatchingIsCorrect() {
    var r = getQuestionDataByIndex_Results(arrayOfQuestions().length);
    if (!r) {
      return false;
    }
    var isCorrect = true;
    Object.keys(r).forEach((k) => {
      if (r[k] != "C") {
        isCorrect = false;
      }
    });
    return isCorrect;
  }
  function getNIncorrectMatching() {
    const results = getQuestionDataByIndex_Results(arrayOfQuestions().length);
    let nIncorrect = 0;

    if (results !== null && results !== undefined) {
      Object.values(results).forEach((value) => {
        if (value !== "C") {
          nIncorrect++;
        }
      });
    }

    return nIncorrect;
  }

  function timeSince(date) {
    const seconds = Math.floor((new Date() - date) / 1000);

    if (seconds < 5) {
      return "Just now";
    }

    let interval = Math.floor(seconds / 31536000);
    if (interval > 1) {
      return `${interval} years ago`;
    }

    interval = Math.floor(seconds / 2592000);
    if (interval > 1) {
      return `${interval} months ago`;
    }

    interval = Math.floor(seconds / 86400);
    if (interval > 1) {
      return `${interval} days ago`;
    }

    interval = Math.floor(seconds / 3600);
    if (interval > 1) {
      return `${interval} hours ago`;
    }

    interval = Math.floor(seconds / 60);
    if (interval > 1) {
      return `${interval} minutes ago`;
    }

    return `${Math.floor(seconds)} seconds ago`;
  }

  function getRgb(color) {
    const [r, g, b] = color
      .replace("rgb(", "")
      .replace(")", "")
      .split(",")
      .map(Number);
    return { r, g, b };
  }

  function colorInterpolate(colorA, colorB, intval) {
    const rgbA = getRgb(colorA);
    const rgbB = getRgb(colorB);

    const colorVal = (prop) =>
      Math.round(rgbA[prop] * (1 - intval) + rgbB[prop] * intval);

    return { r: colorVal("r"), g: colorVal("g"), b: colorVal("b") };
  }

  function getGradeColor(grade = null) {
    const resultsGrade = grade ?? resultsInfo?.grade[1];

    if (resultsGrade <= 60) {
      return "rgb(216, 60, 60)";
    }

    const t = colorInterpolate(
      "rgb(216, 60, 60)",
      "rgb(88, 232, 146)",
      resultsGrade / 100
    );

    return `rgb(${t.r},${t.g},${t.b})`;
  }

  function getMostMissedColor(percent) {
    const t = colorInterpolate(
      "rgb(232, 165, 88)",
      "rgb(216, 60, 60)",
      percent
    );

    return `rgb(${t.r},${t.g},${t.b})`;
  }

  function MostDifficultTerms() {
    var MDTsAllowed = user.membership > 2;
    var MDTs = quizDetails[2];
    var highestMissedTermIdx = highestMissedTermIndex;
    var totalMissedTerms = 0;
    if (!MDTsAllowed) {
      if (setData?.terms?.length > 3) {
        MDTs = [9, 6];
      } else {
        MDTs = [9, 6, 2];
      }
      highestMissedTermIdx = 2;
    }
    Object.keys(MDTs).forEach((key) => {
      totalMissedTerms += MDTs[key];
    });
    return (
      <div className="chart">
        {Object.keys(MDTs).filter((key) => MDTs[key] > 0).length > 0 ? (
          <div
            className="labels"
            style={{
              marginTop: Object.keys(MDTs).length * 5 + "px",
            }}
          >
            {Object.keys(MDTs)
              .filter((key) => MDTs[key] > 0)
              .map(
                (key, c) =>
                  setData.terms[key] != null && (
                    <div className="label">
                      {setData.terms[key][4] == null ? (
                        <h5>{setData.terms[key][0]}</h5>
                      ) : (
                        <StaticMathField>
                          {setData.terms[key][0]}
                        </StaticMathField>
                      )}
                      <div
                        className="chart-part"
                        style={{
                          width: (MDTs[key] / totalMissedTerms) * 70 + "%", //(missed / total
                          backgroundColor: getMostMissedColor(
                            MDTs[key] / highestMissedTermIdx
                          ),
                        }}
                      ></div>
                      {/*
                    <div className="missed-number">
                      <h5
                        className="n-times"
                        style={{
                          color: getMostMissedColor(
                            quizDetails[2][key] /
                              highestMissedTermIndex
                          ),
                        }}
                      >
                        {quizDetails[2][key] +
                          " time" +
                          (quizDetails[2][key] > 1
                            ? "s"
                            : "") +
                          ": "}
                      </h5>
                      {setData.terms[key][5] == null ? (
                        <h5 className="missed-definition">
                          {setData.terms[key][1]}
                        </h5>
                      ) : (
                        <StaticMathField>
                          {setData.terms[key][1]}
                        </StaticMathField>
                      )}
                    </div>
                      */}
                    </div>
                  )
              )}
          </div>
        ) : (
          <p
            className="label"
            style={{
              //marginTop: "20px",
              textAlign: "center",
            }}
          >
            Hmm, there's nothing to display here, which means you must really
            know your material!
          </p>
        )}
      </div>
    );
  }

  function ConfusedTerms() {
    var confusedTerms = quizDetails[4];
    if (!user?.plans?.analytics) {
      confusedTerms = [
        [3, 1],
        [2, 0],
      ];
    }
    return Object.keys(confusedTerms).map((key, count) => (
      <>
        <div className="confused-terms">
          {setData.terms[key] != undefined &&
            setData.terms[key][0] != undefined && (
              <>
                <Tooltip
                  content={
                    "You seem to be confused about the definition of this term."
                  }
                  direction="top"
                >
                  <div
                    className={
                      "confused-word confused-term " +
                      (setData.terms[key][5] != undefined
                        ? "confused-word-equation"
                        : "")
                    }
                    id={"confused-term-" + count}
                  >
                    {setData.terms[key][5] != undefined ? (
                      <StaticMathField>{setData.terms[key][0]}</StaticMathField>
                    ) : (
                      <p>{setData.terms[key][0]}</p>
                    )}
                  </div>
                </Tooltip>
                <div className={"confused-definition"}>
                  {confusedTerms[key].map(
                    (value, count2) =>
                      setData?.terms[value] && (
                        <>
                          <Xarrow
                            start={"confused-term-" + count}
                            end={
                              "incorrect-def-count:" +
                              count +
                              "-count2:" +
                              count2
                            }
                            showHead={false}
                            color={"#D83C3C"}
                            startAnchor={"middle"}
                          />
                          <Tooltip
                            content={
                              "When asked to define " +
                              setData.terms[key][0] +
                              ", you picked this definition, which is incorrect."
                            }
                            direction="top"
                          >
                            <div
                              className={
                                "confused-word confused-definition-incorrect " +
                                (setData?.terms[key][5] != undefined
                                  ? "confused-word-equation"
                                  : "")
                              }
                              id={
                                "incorrect-def-count:" +
                                count +
                                "-count2:" +
                                count2
                              }
                            >
                              {setData?.terms[value] &&
                              setData?.terms[value][5] != undefined ? (
                                <StaticMathField>
                                  {setData?.terms[value][1]}
                                </StaticMathField>
                              ) : (
                                setData?.terms[value] && (
                                  <p>{setData?.terms[value][1]}</p>
                                )
                              )}
                            </div>
                          </Tooltip>
                        </>
                      )
                  )}
                  {/* Correct Def */}

                  <Xarrow
                    start={"confused-term-" + count}
                    end={"correct-def-" + count}
                    showHead={false}
                    color={"#58E892"}
                    dashness={true}
                    path={"straight"}
                    startAnchor={"middle"}
                  />
                  <Tooltip
                    content={
                      "This is the correct definition of " +
                      setData.terms[key][0]
                    }
                    direction="top"
                  >
                    <div
                      className="confused-word confused-definition-correct"
                      id={"correct-def-" + count}
                    >
                      {setData.terms[key][5] != undefined ? (
                        <StaticMathField>
                          {setData.terms[key][1]}
                        </StaticMathField>
                      ) : (
                        <p>{setData.terms[key][1]}</p>
                      )}
                    </div>
                  </Tooltip>
                </div>
              </>
            )}
        </div>
      </>
    ));
  }

  const updateQuizDB = function (fn = function () {}) {
    overwrite_quiz_answers(setid, quizQueue, (data) => {
      if (fn) {
        fn(data);
      }
    });
  };

  const updateQuizInfo = () => {
    get_quiz_info(setid, (data) => {
      if ((data.data && data.data.error) || data.error) {
        setIsTakingQuiz(false);
        setSettingsVisible(true);
      } else {
        if (data.assigned_quiz_awaiting_start) {
          const pattern = /results$/;
          const currentPath = window.location.pathname;
          if (!pattern.test(currentPath)) {
            setIsAwaitingStart(true);
          }
        } else {
          if (!data.submitted && !viewingResults) {
            setDeadline(data.deadline);
            // Set the initial deadline

            const time = Date.parse(data.deadline) - Date.now();
            setDays(Math.floor(time / (1000 * 60 * 60 * 24)));
            setHours(Math.floor((time / (1000 * 60 * 60)) % 24));
            setMinutes(Math.floor((time / 1000 / 60) % 60));
            setSeconds(Math.floor((time / 1000) % 60));
            setIsTakingQuiz(true);
            setQuizQueue(data.data);
            setQuizId(data.id);
          } else {
            if (!viewingResults) {
              // takes you to most recent quiz answers
              navigate("./" + data.id + "/results");
              window.location.reload();
            }
          }
          setOptionsResponse(data.options);
        }
      }
    });
  };

  const isMobile = () => {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  };

  const doughnutChartConfig = {
    labels: [
      resultsInfo?.grade[1] + "% correct",
      100 - resultsInfo?.grade[1] + "% incorrect",
    ],
    ignoreTraditionalLabelCallback: true,
    ignoreLegend: true,
    //label: resultsInfo?.grade[1] + "%",
    data: [resultsInfo?.grade[1], 100 - resultsInfo?.grade[1]],
    bgColor: [getGradeColor(), "#363636"],
    textColor: "#71EAA2",
    textInside: resultsInfo?.grade[2],
    rotate: 0,
    divider: 2.6,
    minFontSize: isMobile() ? 14 : 20,
    maxFontSize: isMobile() ? 18 : 22,
  };
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isTakingQuiz) {
      if (intervalRef.current) {
        // Clear any previous interval if it exists
        clearInterval(intervalRef.current);
      }

      // Set up the new interval
      intervalRef.current = setInterval(() => getTime(deadline), 1000);
    }

    return () => {
      // Clear the interval when the component is unmounted or when isTakingQuiz or deadline changes
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [deadline, isTakingQuiz]);

  useEffect(() => {
    get_set_data(
      setid,
      get_session_token(),
      (data) => {
        setSetData(data);
        setNQuestions(Math.min(20, data.terms.length));
      },
      "quiz"
    );
    updateQuizInfo();
  }, []);

  useEffect(() => {
    if (isTakingQuiz) {
      setProgress(getProgress()[0]);
    }
  }, [quizQueue, isTakingQuiz]);

  useEffect(() => {
    if (isTakingQuiz || viewingResults) {
      updateMatchDataForCurrentQuestion();
    }
  }, [currentQuestion, isTakingQuiz, viewingResults, quizQueue]);

  useEffect(() => {
    if (isTakingQuiz) {
      if (optionsResponse["audio"] == "1") {
        if (
          (optionsResponse["answer_with"] == "0"
            ? setData?.terms[getCurrentQuestionData()?.term][4]
            : setData?.terms[getCurrentQuestionData()?.term][5]) == null
        ) {
          if (getCurrentQuestionData().type != "matching") {
            var term =
              setData?.terms[getCurrentQuestionData()?.term][
                optionsResponse["answer_with"] == "0" ? 0 : 1
              ];

            cancel();
            if (getCurrentQuestionData().type == "mc") {
              speak("Pick the correct multiple choice answer for: " + term);
            } else if (getCurrentQuestionData().type == "t/f") {
              var def =
                setData?.terms[getCurrentQuestionData()?.response][
                  optionsResponse["answer_with"] == "0" ? 0 : 1
                ];
              speak("True or false? " + term + " goes with " + def);
            } else {
              speak({ text: "Define the term: " + term });
            }
          }
        }
      }
    }
  }, [currentQuestion]);

  useEffect(() => {
    if (id) {
      fetch_quiz_info(setid, id, (data) => {
        // get quiz info of a past test from session id param
        if (data.success) {
          setIsOnMainViewingResultsPage(true);
          setViewingResults(true);
          setIsTakingQuiz(false);
          setResultsInfo(data);
          setQuizQueue(data.data);
          setSubmitQuizPopup(false);
          if (data.grade[1] >= 90) {
            setShareQuizScoreEnabled(true);
          }
        } else {
          navigate("../");
        }
      });
    }
  }, [isTakingQuiz]);

  const [checking, setchecking] = useState(nQuestions < 3 ? false : true);
  const [mcqCheck, setmcqCheck] = useState(nQuestions < 5 ? false : true);
  useEffect(() => {
    // Autosave answers
    if (lastAnswerChanged != null) {
      var dt = new Date() - lastAnswerChanged;
      if (dt >= 1000) {
        setLastAnswerChanged(null);
        updateQuizDB();
        setLastAutosaveTime(new Date());
      }
    }
    if (settingsVisible == true) {
      let bodys = document.querySelector("html");
      bodys.style.overflow = "hidden";
    } else {
      let bodys = document.querySelector("html");

      bodys.style.overflow = "auto";
    }
  });
  let toggleQuiz = () => {
    setquizView(true);
  };
  let inactiveQuiz = () => {
    setquizView(false);
  };

  let toggleChecks = () => {
    if (nQuestions < 3) {
      setchecking(false);
    } else {
      setchecking(true);
    }
  };
  let toggleMcqChecks = () => {
    if (nQuestions < 5) {
      setmcqCheck(false);
    } else {
      setmcqCheck(true);
    }
  };
  let checkquiz = () => {
    if (checking == true) {
      setchecking(false);
    } else {
      setchecking(true);
    }
  };
  let checkMcq = () => {
    if (nQuestions < 5) {
      setmcqCheck(false);
      return;
    }
    if (mcqCheck == true) {
      setmcqCheck(false);
    } else {
      setmcqCheck(true);
    }
  };

  useEffect(() => {
    toggleChecks();
    toggleMcqChecks();
    checkquiz();
    checkMcq();
  }, [nQuestions]);

  useEffect(() => {
    get_tutorial_progress("quiz", (data) => {
      if (data["success"] !== undefined) {
        setQuizTutorialPhase(data["phase"]);
        setQuizTutorialStepIndex(data["step_index"]);
        if (isTakingQuiz && data["phase"] < 2) {
          setQuizTutorialEnabled(true);
        }
        if (viewingResults && data["phase"] < 2) {
          setQuizTutorialPhase(1);
          setQuizTutorialEnabled(true);
        }
      }
    });
    get_tutorial_progress("quizzyplus", (data) => {
      if (data["success"] !== undefined) {
        setQuizzyPlusQuizTutorialPhase(data["phase"]);
        setQuizzyPlusQuizTutorialStepIndex(data["step_index"]);
        setQuizzyPlusQuizTutorialEnabled(true);
      }
    });
  }, [isTakingQuiz, viewingResults]);

  return (
    <div
      className={
        quizView ? "quiz-exam-container no-pad" : "quiz-exam-container"
      }
    >
      {quizTutorialEnabled && (
        <QuizTutorial
          phase={quizTutorialPhase}
          setPhase={setQuizTutorialPhase}
          user={user}
          viewingResults={viewingResults}
          stepIndex={quizTutorialStepIndex}
          setStepIndex={setQuizTutorialStepIndex}
          setIsOnMainViewingResultsPage={setIsOnMainViewingResultsPage}
          isOnMainViewingResultsPage={isOnMainViewingResultsPage}
        ></QuizTutorial>
      )}
      {quizzyPlusQuizTutorialEnabled && !quizTutorialEnabled && (
        <QuizzyPlusTutorial
          phase={quizzyPlusQuizTutorialPhase}
          setPhase={setQuizzyPlusQuizTutorialPhase}
          user={user}
          stepIndex={quizzyPlusQuizTutorialStepIndex}
          setStepIndex={setQuizzyPlusQuizTutorialStepIndex}
        ></QuizzyPlusTutorial>
      )}

      <ShareQuizScore
        shareQuizScoreEnabled={shareQuizScoreEnabled}
        setShareQuizScoreEnabled={setShareQuizScoreEnabled}
        shareUrl="https://quizzynow.com/study/166/leadership-in-management-"
      ></ShareQuizScore>
      <FoldedPopup
        noImage={true}
        closeButton={true}
        enabled={isAwaitingStart}
        setEnabled={setIsAwaitingStart}
        className="quiz-scheduled-popup"
      >
        <h1>Take your assigned Quiz</h1>
        <p>You have a Quiz assigned right now. Take it to stay on track.</p>
        <CTAButton
          text="Begin Quiz"
          color="purple"
          onClick={() => {
            begin_assigned_quiz(setData.id, () => {
              updateQuizInfo();
              setIsAwaitingStart(false);
            });
          }}
        ></CTAButton>

        <p
          className="no-thanks"
          onClick={() => {
            const currentURL = window.location.href;
            const urlWithoutLastSegment = currentURL.substring(
              0,
              currentURL.lastIndexOf("/")
            );
            window.location.href = urlWithoutLastSegment;
          }}
        >
          Go back
        </p>
      </FoldedPopup>
      <Popup
        enabled={submitQuizPopup}
        setEnabled={setSubmitQuizPopup}
        variant="ctaPopupWithIcon"
        title="Submit this Quiz?"
        cta="Submit"
        color="green"
        ctaActivate={() => {
          if (!submittedQuiz) {
            updateQuizDB((d) => {
              submit_quiz(setid, (data) => {
                setSubmittedQuiz(true);
                if (data["success"]) {
                  navigate("./" + d["id"] + "/results");
                  window.location.reload();
                }
              });
            });
          }
        }}
        ctaIcon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="23.915"
            height="17.266"
            viewBox="0 0 23.915 17.266"
          >
            <path
              id="done_FILL1_wght400_GRAD0_opsz48"
              d="M15.929,29.466,7.7,21.237l1.58-1.58,6.649,6.649L30.036,12.2l1.58,1.58Z"
              transform="translate(-7.7 -12.2)"
              fill="#fff"
            />
          </svg>
        }
        subtitle={
          "By clicking the Submit button, this Quiz will be submitted and graded."
        }
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="86.661"
            height="62.567"
            viewBox="0 0 86.661 62.567"
          >
            <path
              id="done_FILL1_wght400_GRAD0_opsz48"
              d="M37.519,74.767,7.7,44.948l5.724-5.724L37.519,63.318,88.637,12.2l5.724,5.724Z"
              transform="translate(-7.7 -12.2)"
              fill="#58e892"
            />
          </svg>
        }
      ></Popup>
      {settingsVisible && !isTakingQuiz && setData?.terms?.length > 1 && (
        <div id="opt" className="recall-session-option">
          <div className="recall-session-option-main">
            <div className="container">
              <div className="heading-cont">
                {/* <div className="back" onClick={() => navigate(-1)}>
                  button
                </div> */}

                <h3 className="title">Create Practice Quiz</h3>
                <p className="sub-title">
                  You can customize your practice quiz here
                </p>
              </div>

              <div className="container-top">
                <div className="container-top-radio">
                  <p>Audio</p>
                  <div className="radio-container" ref={audioRadio}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "5px",
                      }}
                      className="audio-div"
                    >
                      <input id="audio-on" type="radio" name="audio-enabled" />
                      <label for="audio-on">On</label>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "5px",
                      }}
                    >
                      <input
                        id="audio-off"
                        type="radio"
                        name="audio-enabled"
                        defaultChecked={"false"}
                      />
                      <label for="audio-off">Off</label>
                    </div>
                  </div>
                </div>
                <div className="container-top-radio">
                  <p>Answer with</p>
                  <div className="radio-container" ref={answerWithRadio}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "5px",
                      }}
                    >
                      <input id="term" type="radio" name="answer-with" />
                      <label for="term">Term</label>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "5px",
                      }}
                    >
                      <input
                        id="def"
                        type="radio"
                        name="answer-with"
                        defaultChecked={"on"}
                      />
                      <label for="def">Definition</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="container-center">
                <div className="container-top-radio">
                  <p>Types of questions</p>
                  <div
                    className="radio-container"
                    ref={typesofQuestionsCheckboxes}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "5px",
                      }}
                    >
                      <input
                        id="tf"
                        type="checkbox"
                        name="types-of-questions"
                        disabled={nQuestions < 3}
                        checked={checking}
                        //defaultChecked={checking}
                        onChange={checkquiz}
                      />
                      <label for="tf">True/False</label>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "5px",
                      }}
                    >
                      <input
                        id="mc"
                        type="checkbox"
                        name="types-of-questions"
                        disabled={nQuestions < 5}
                        checked={mcqCheck}
                        //defaultChecked={mcqCheck}
                        onChange={checkMcq}
                      />
                      <label for="mc">Multiple Choice</label>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "5px",
                      }}
                    >
                      <input
                        id="matching"
                        type="checkbox"
                        name="types-of-questions"
                        defaultChecked={true}
                      />
                      <label for="matching">Matching</label>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "5px",
                      }}
                    >
                      <input
                        id="short-answer"
                        type="checkbox"
                        name="types-of-questions"
                        defaultChecked={true}
                      />
                      <label for="short-answer">Short Answer</label>
                    </div>
                  </div>
                </div>
                <div
                  id="types-of-terms-images-div"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "30px",
                  }}
                >
                  <div className="container-top-radio">
                    <p>Types of terms</p>
                    <div className="radio-container" ref={typesOfTermsRadio}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: "5px",
                        }}
                      >
                        <input
                          id="all-terms"
                          type="radio"
                          name="types-of-terms"
                          defaultChecked={"checked"}
                          onChange={(e) => {
                            setMDTsChecked(false);
                          }}
                        />
                        <label for="all-terms">All terms</label>{" "}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: "5px",
                          cursor:
                            user.membership <= 2 ? "not-allowed" : "pointer",
                        }}
                      >
                        <input
                          id="mdts"
                          type="radio"
                          name="types-of-terms"
                          disabled={
                            Object.keys(setData?.psd?.most_difficult_terms)
                              .length === 0 || user.membership <= 2
                          }
                          onChange={(e) => {
                            if (user.membership > 2) {
                              setMDTsChecked(true);
                              setNQuestions(
                                Object.keys(setData?.psd?.most_difficult_terms)
                                  .length
                              );
                            }
                          }}
                        />
                        <label for="mdts">Most Difficult Terms</label>{" "}
                        <PlusIcon />
                      </div>
                    </div>
                  </div>
                  <div className="container-top-radio">
                    <p>Images</p>
                    <div className="radio-container" ref={imagesRadio}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: "5px",
                        }}
                      >
                        <input
                          id="images-on"
                          type="radio"
                          name="images-options"
                          defaultChecked={"on"}
                        />
                        <label for="images-on">On</label>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: "5px",
                        }}
                      >
                        <input
                          id="images-off"
                          type="radio"
                          name="images-options"
                        />
                        <label for="images-off">Off</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="container-center vbg">
                <div className="radio-container" ref={nQuestionsForm}>
                  <p>Number of Questions</p>
                  <input
                    id="n-questions"
                    type="number"
                    defaultValue={
                      mdtsChecked
                        ? Object.keys(setData.psd.most_difficult_terms).length
                        : Math.min(20, setData?.terms?.length)
                    }
                    value={nQuestions}
                    min={1}
                    max={
                      mdtsChecked
                        ? Object.keys(setData.psd.most_difficult_terms).length
                        : setData?.terms?.length
                    }
                    onChange={() => {
                      setNQuestions(nQuestionsForm.current.children[1].value);
                    }}
                    onKeyUp={() => {
                      toggleChecks();
                      toggleMcqChecks();
                    }}
                    onClick={() => {
                      toggleChecks();
                      toggleMcqChecks();
                    }}
                  ></input>
                </div>
                <div className="radio-container" ref={timeSelection}>
                  <p>Time</p>
                  <select name="time" id="time" defaultValue={"30 min"}>
                    <option value="5 min">5 minutes</option>
                    <option value="10 min">10 minutes</option>
                    <option value="15 min">15 minutes</option>
                    <option value="20 min">20 minutes</option>
                    <option value="25 min">25 minutes</option>
                    <option value="30 min">30 minutes</option>
                    <option value="45 min">45 minutes</option>
                    <option value="1 hour">1 hour</option>
                    <option value="2 hours">2 hours</option>
                    <option value="5 hours">5 hours</option>
                    <option value="7 hours">7 hours</option>
                    <option value="9 hours">9 hours</option>
                    <option value="12 hours">12 hours</option>
                    <option value="1 day">1 day</option>
                    <option value="2 days">2 days</option>
                    <option value="3 days">3 days</option>
                    <option value="4 days">4 days</option>
                    <option value="5 days">5 days</option>
                    <option value="6 days">6 days</option>
                    <option value="7 days">7 days</option>
                  </select>
                </div>
              </div>
              {/*<div className="container-center">
                <div className="radio-container" ref={emailForm}>
                  <p>Email results</p>
                  <input
                    className="email-input"
                    placeholder="Enter email address.."
                    type="text"></input>
                </div>
                  </div>*/}
              <div className="create-btn-cont">
                <CTAButton
                  color="purple"
                  text="Create"
                  className={
                    "create-btn" +
                    ((mdtsChecked == true && user.membership == 0) ||
                    !createQuizButtonEnabled
                      ? " create-btn-disabled"
                      : "")
                  }
                  onClick={() => {
                    var answer_with_term = 0;
                    var audio = 0;
                    var types_of_questions = [];
                    var types_of_terms = mdtsChecked ? 1 : 0;
                    var images = 0;
                    var n_questions = nQuestionsForm.current.children[1].value;
                    var time = timeSelection.current.children[1].value;
                    var email = ""; //emailForm.current.children[1].value;

                    if (
                      (mdtsChecked == true && user.membership == 0) ||
                      !createQuizButtonEnabled
                    ) {
                      return false;
                    }
                    if (
                      answerWithRadio.current.children[0].children[0].checked ==
                      true
                    ) {
                      answer_with_term = 1;
                    }
                    if (
                      audioRadio.current.children[0].children[0].checked == true
                    ) {
                      audio = 1;
                    }

                    if (
                      imagesRadio.current.children[0].children[0].checked ==
                      true
                    ) {
                      images = 1;
                    }

                    var t_f_questions =
                      typesofQuestionsCheckboxes.current.children[0].children[0]
                        .checked == true
                        ? 1
                        : 0;
                    var mc_questions =
                      typesofQuestionsCheckboxes.current.children[1].children[0]
                        .checked == true
                        ? 1
                        : 0;
                    var matching =
                      typesofQuestionsCheckboxes.current.children[2].children[0]
                        .checked == true
                        ? 1
                        : 0;
                    var saq =
                      typesofQuestionsCheckboxes.current.children[3].children[0]
                        .checked == true
                        ? 1
                        : 0;

                    types_of_questions.push(t_f_questions);
                    types_of_questions.push(mc_questions);
                    types_of_questions.push(matching);
                    types_of_questions.push(saq);

                    setCreateQuizButtonEnabled(false);

                    create_practice_exam(
                      setid,
                      audio,
                      answer_with_term,
                      types_of_questions,
                      types_of_terms,
                      images,
                      n_questions,
                      time,
                      email,
                      (data) => {
                        updateQuizInfo();
                        setQuizQueue(data.data);
                        setIsTakingQuiz(true);
                        setSettingsVisible(false);
                        setDeadline(data.deadline);
                      }
                    );
                  }}
                ></CTAButton>
                {mdtsChecked == true && user.membership == 0 && (
                  <PlusIcon style={{ marginLeft: "10px" }} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {((settingsVisible && !isTakingQuiz) ||
        (viewingResults && resultsInfo?.grade == undefined) ||
        isAwaitingStart) && ( //placeholders
        <QuizExamPlaceholders></QuizExamPlaceholders>
      )}

      {!settingsVisible && !isAwaitingStart && (
        <>
          {!isOnMainViewingResultsPage &&
            Object.keys(matches).map((index) => (
              <div className="arrow">
                {" "}
                <Xarrow
                  start={index}
                  end={matches[index][0]}
                  lineColor={getMatchingLineColor(index)} //"#8358e8"
                  showHead={false}
                />
              </div>
            ))}
          <div className="quiz-exam-top">
            <div className="navbars-left quiz-opt">
              <img
                onClick={toggleQuiz}
                className="hotdog-nav"
                src="/images/general/threelines.svg"
                alt=""
              />
            </div>
            <div className="quiz-exam-top-left none">
              <h3 className="title">
                <svg
                  style={{ marginRight: "10px" }}
                  xmlns="http://www.w3.org/2000/svg"
                  width="19.732"
                  height="13.1"
                  viewBox="0 0 19.732 13.1"
                  onClick={() => {
                    window.location.href = "../../";
                  }}
                >
                  <path
                    id="keyboard_backspace_FILL0_wght400_GRAD0_opsz48"
                    d="M12.55,25.1,6,18.55,12.55,12l1.178,1.178L9.179,17.728H25.732v1.644H9.179l4.549,4.549Z"
                    transform="translate(-6 -12)"
                    fill="#2b2b2b"
                  />
                </svg>
                {setData?.title + " - Practice Quiz"}
              </h3>
              {viewingResults ? (
                <h5 className="sub-title">{"Finished Exam"}</h5>
              ) : (
                <h5 className="sub-title">{getPart()}</h5>
              )}
              {viewingResults ? (
                <h5
                  className="sub-title viewing-results-label"
                  style={{ color: getGradeColor() }}
                >
                  {"Viewing Results"}
                </h5>
              ) : (
                <h5 className="sub-title">
                  {"Last saved: " + timeSince(lastAutosaveTime)}
                </h5>
              )}
            </div>
            {viewingResults ? (
              <div className="quiz-finished-top-center none">
                <div className="progress-cont">
                  <h3 className="score-title">Your score</h3>

                  <ProgressBar
                    borderRadius={50}
                    completed={
                      resultsInfo?.grade[1] == undefined
                        ? 0
                        : resultsInfo?.grade[1]
                    }
                    isLabelVisible={false}
                    bgColor={getGradeColor()}
                    height="15px"
                  />

                  <p
                    className="progress-percent"
                    style={{ color: getGradeColor() }}
                  >
                    {resultsInfo?.grade[1] + "% correct"}
                  </p>
                </div>
                <h3 className="grade" style={{ color: getGradeColor() }}>
                  {resultsInfo?.grade[2]}
                </h3>
              </div>
            ) : (
              <div className="quiz-exam-top-center none">
                <ProgressBar
                  completed={progress}
                  isLabelVisible={false}
                  bgColor="#8358E8"
                  height="15px"
                />
                <p className="progress-percent">{progress + "%"}</p>
              </div>
            )}
            <div className="quiz-exam-center mbl-view">
              {!isOnMainViewingResultsPage && (
                <div className="quiz-exam-center-left none">
                  <h4 className="title">Questions</h4>
                  <div className="questions-container">
                    {/* Question list on the left */}

                    {viewingResults
                      ? arrayOfQuestions().map((num) => (
                          <div
                            className={
                              (getQuestionDataByIndex_Results(num) == "C"
                                ? "viewing-results-correct"
                                : "viewing-results-incorrect") +
                              " question viewing-results " +
                              (currentQuestion == num
                                ? "viewing-results-current"
                                : "")
                            }
                            onClick={() => {
                              setCurrentQuestion(num);
                            }}
                          >
                            {num + 1}
                          </div>
                        ))
                      : arrayOfQuestions().map((num) => (
                          <div
                            className={
                              (currentQuestion == num
                                ? "question-current"
                                : (!Array.isArray(
                                    getQuestionDataByIndex(num)?.answer
                                  ) &&
                                    getQuestionDataByIndex(num)?.answer !==
                                      null &&
                                    getQuestionDataByIndex(num)?.answer !==
                                      "") ||
                                  getQuestionDataByIndex(num)?.answer?.length >
                                    0
                                ? "question-answered"
                                : "") + " question"
                            }
                            onClick={() => {
                              setCurrentQuestion(num);
                            }}
                          >
                            {num + 1}
                          </div>
                        ))}

                    {/* matching */}

                    {viewingResults &&
                    quizQueue[3] != undefined &&
                    quizQueue[3].length > 0 ? (
                      <div
                        className={
                          "viewing-results " +
                          (checkIfAllMatchingIsCorrect() == true
                            ? "viewing-results-correct"
                            : "viewing-results-incorrect") +
                          " question matching-question " +
                          (currentQuestion == arrayOfQuestions().length
                            ? "viewing-results-current"
                            : "")
                        }
                        onClick={() => {
                          setCurrentQuestion(arrayOfQuestions().length);
                        }}
                      >
                        {arrayOfQuestions().length + 1 + "-" + getProgress()[1]}
                      </div>
                    ) : (
                      quizQueue[3] != undefined &&
                      quizQueue[3].length > 0 && (
                        <div
                          className={
                            (currentQuestion == arrayOfQuestions().length
                              ? "question-current"
                              : "") +
                            (getProgress()[1] - arrayOfQuestions().length ===
                            Object.keys(quizQueue[3][0].answer).length
                              ? " question-answered"
                              : "") +
                            " question matching-question"
                          }
                          onClick={() => {
                            setCurrentQuestion(arrayOfQuestions().length);
                          }}
                        >
                          {arrayOfQuestions().length +
                            1 +
                            "-" +
                            getProgress()[1]}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className={quizView ? "mobile-quiz" : "mobile-quiz hide-it"}>
              <div onClick={inactiveQuiz} className="exe">
                x
              </div>
              <div className="quiz-exam-top-left">
                <h3 className="title">
                  <svg
                    style={{ marginRight: "10px" }}
                    xmlns="http://www.w3.org/2000/svg"
                    width="19.732"
                    height="13.1"
                    viewBox="0 0 19.732 13.1"
                    onClick={() => {
                      window.location.href = !viewingResults
                        ? "../../"
                        : "../../";
                    }}
                  >
                    <path
                      id="keyboard_backspace_FILL0_wght400_GRAD0_opsz48"
                      d="M12.55,25.1,6,18.55,12.55,12l1.178,1.178L9.179,17.728H25.732v1.644H9.179l4.549,4.549Z"
                      transform="translate(-6 -12)"
                      fill="#2b2b2b"
                    />
                  </svg>
                  {setData?.title + " - Practice Quiz"}
                </h3>
                {viewingResults ? (
                  <h5 className="sub-title">{"Finished Exam"}</h5>
                ) : (
                  <h5 className="sub-title">{getPart()}</h5>
                )}
                {viewingResults ? (
                  <h5 className="sub-title viewing-results-label">
                    {"Viewing Results"}
                  </h5>
                ) : (
                  <h5 className="sub-title">
                    {"Last saved: " + timeSince(lastAutosaveTime)}
                  </h5>
                )}
              </div>
              {viewingResults ? (
                <div className="quiz-finished-top-center">
                  <div className="progress-cont">
                    <h3 className="score-title">Your score</h3>
                    <ProgressBar
                      borderRadius={50}
                      completed={
                        resultsInfo?.grade[1] == undefined
                          ? 0
                          : resultsInfo?.grade[1]
                      }
                      isLabelVisible={false}
                      bgColor={getGradeColor()}
                      height="15px"
                    />
                    <p
                      className="progress-percent"
                      style={{ color: getGradeColor() }}
                    >
                      {resultsInfo?.grade[1] + "% correct"}
                    </p>
                  </div>
                  <h3 className="grade" style={{ color: getGradeColor() }}>
                    {resultsInfo?.grade[2]}
                  </h3>
                </div>
              ) : (
                <div className="quiz-exam-top-center">
                  <ProgressBar
                    completed={progress}
                    isLabelVisible={false}
                    bgColor="#8358E8"
                    height="15px"
                  />
                  <p className="progress-percent">{progress + "%"}</p>
                </div>
              )}
              <div className="quiz-exam-center mbl-view">
                {!isOnMainViewingResultsPage && (
                  <div className="quiz-exam-center-left">
                    <h4 className="title">Questions</h4>
                    <div className="questions-container">
                      {/* Question list on the left */}

                      {viewingResults
                        ? arrayOfQuestions().map((num) => (
                            <div
                              className={
                                (getQuestionDataByIndex_Results(num) == "C"
                                  ? "viewing-results-correct"
                                  : "viewing-results-incorrect") +
                                " question viewing-results " +
                                (currentQuestion == num
                                  ? "viewing-results-current"
                                  : "")
                              }
                              onClick={() => {
                                setCurrentQuestion(num);
                              }}
                            >
                              {num + 1}
                            </div>
                          ))
                        : arrayOfQuestions().map((num) => (
                            <div
                              className={
                                (currentQuestion == num
                                  ? "question-current"
                                  : (!Array.isArray(
                                      getQuestionDataByIndex(num)?.answer
                                    ) &&
                                      getQuestionDataByIndex(num)?.answer !==
                                        null &&
                                      getQuestionDataByIndex(num)?.answer !==
                                        "") ||
                                    getQuestionDataByIndex(num)?.answer
                                      ?.length > 0
                                  ? "question-answered"
                                  : "") + " question"
                              }
                              onClick={() => {
                                setCurrentQuestion(num);
                              }}
                            >
                              {num + 1}
                            </div>
                          ))}

                      {/* matching */}

                      {viewingResults &&
                      quizQueue[3] != undefined &&
                      quizQueue[3].length > 0 ? (
                        <div
                          className={
                            "viewing-results " +
                            (checkIfAllMatchingIsCorrect() == true
                              ? "viewing-results-correct"
                              : "viewing-results-incorrect") +
                            " question matching-question " +
                            (currentQuestion == arrayOfQuestions().length
                              ? "viewing-results-current"
                              : "")
                          }
                          onClick={() => {
                            setCurrentQuestion(arrayOfQuestions().length);
                          }}
                        >
                          {arrayOfQuestions().length +
                            1 +
                            "-" +
                            getProgress()[1]}
                        </div>
                      ) : (
                        quizQueue[3] != undefined &&
                        quizQueue[3].length > 0 && (
                          <div
                            className={
                              (currentQuestion == arrayOfQuestions().length
                                ? "question-current"
                                : "") +
                              (getProgress()[1] - arrayOfQuestions().length ===
                              Object.keys(quizQueue[3][0].answer).length
                                ? " question-answered"
                                : "") +
                              " question matching-question"
                            }
                            onClick={() => {
                              setCurrentQuestion(arrayOfQuestions().length);
                            }}
                          >
                            {arrayOfQuestions().length +
                              1 +
                              "-" +
                              getProgress()[1]}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="quiz-exam-top-right">
              {!viewingResults ? (
                <>
                  <div className="stop-watch-cont">
                    <CiStopwatch className="stop-watch-icon" />
                  </div>{" "}
                  <h3 className="time-left">{getTimerString()}</h3>
                </>
              ) : isViewingDetails ? (
                <button
                  className="another-quiz-btn"
                  onClick={() => {
                    setIsOnMainViewingResultsPage(true);
                    setIsViewingDetails(false);
                  }}
                >
                  <div className="arrow-icon-cont">
                    <BsArrowLeftShort className="arrow-icon" />
                  </div>{" "}
                  Summary
                </button>
              ) : (
                <button
                  className="another-quiz-btn"
                  onClick={() => {
                    retake_quiz(setid, () => {
                      window.location.href = "../../quiz/";
                      //navigate("/quiz");
                    });
                  }}
                >
                  <div className="arrow-icon-cont">
                    <BsArrowLeftShort className="arrow-icon" />
                  </div>{" "}
                  Create New Quiz
                </button>
              )}
            </div>
          </div>
          <div className="quiz-exam-center">
            <div className="desk-view">
              {!isOnMainViewingResultsPage && (
                <div className="quiz-exam-center-left">
                  <h4 className="title">Questions</h4>
                  <div className="questions-container">
                    {/* Question list on the left */}

                    {viewingResults
                      ? arrayOfQuestions().map((num) => (
                          <div
                            className={
                              (getQuestionDataByIndex_Results(num) == "C"
                                ? "viewing-results-correct"
                                : "viewing-results-incorrect") +
                              " question viewing-results " +
                              (currentQuestion == num
                                ? "viewing-results-current"
                                : "")
                            }
                            onClick={() => {
                              setCurrentQuestion(num);
                            }}
                          >
                            {num + 1}
                          </div>
                        ))
                      : arrayOfQuestions().map((num) => (
                          <div
                            className={
                              (currentQuestion == num
                                ? "question-current"
                                : (!Array.isArray(
                                    getQuestionDataByIndex(num)?.answer
                                  ) &&
                                    getQuestionDataByIndex(num)?.answer !==
                                      null &&
                                    getQuestionDataByIndex(num)?.answer !==
                                      "") ||
                                  getQuestionDataByIndex(num)?.answer?.length >
                                    0
                                ? "question-answered"
                                : "") + " question"
                            }
                            onClick={() => {
                              setCurrentQuestion(num);
                            }}
                          >
                            {num + 1}
                          </div>
                        ))}

                    {/* matching */}

                    {viewingResults &&
                    quizQueue[3] != undefined &&
                    quizQueue[3].length > 0 ? (
                      <div
                        className={
                          "viewing-results " +
                          (checkIfAllMatchingIsCorrect() == true
                            ? "viewing-results-correct"
                            : "viewing-results-incorrect") +
                          " question matching-question " +
                          (currentQuestion == arrayOfQuestions().length
                            ? "viewing-results-current"
                            : "")
                        }
                        onClick={() => {
                          setCurrentQuestion(arrayOfQuestions().length);
                        }}
                      >
                        {arrayOfQuestions().length + 1 + "-" + getProgress()[1]}
                      </div>
                    ) : (
                      quizQueue[3] != undefined &&
                      quizQueue[3].length > 0 && (
                        <div
                          className={
                            (currentQuestion == arrayOfQuestions().length
                              ? "question-current"
                              : "") +
                            (getProgress()[1] - arrayOfQuestions().length ===
                            Object.keys(quizQueue[3][0].answer).length
                              ? " question-answered"
                              : "") +
                            " question matching-question"
                          }
                          onClick={() => {
                            setCurrentQuestion(arrayOfQuestions().length);
                          }}
                        >
                          {arrayOfQuestions().length +
                            1 +
                            "-" +
                            getProgress()[1]}
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>

            {setData?.terms[getCurrentQuestionData()?.term] != undefined &&
              !isOnMainViewingResultsPage && (
                <div className="quiz-exam-center-right">
                  {viewingResults &&
                    getCurrentQuestionData()?.type == "matching" && (
                      <div className="view-correct-div">
                        <WhiteButton
                          text={
                            isViewingCorrectAnswersMatching
                              ? "View Original"
                              : "View Correct"
                          }
                          onClick={() => {
                            //updateMatchDataForCorrectAnswers()
                            var newMatching = isViewingCorrectAnswersMatching
                              ? false
                              : true;
                            setIsViewingCorrectAnswersMatching(newMatching);
                            if (newMatching == true) {
                              updateMatchDataForCorrectAnswers();
                            } else {
                              updateMatchDataForCurrentQuestion();
                            }
                          }}
                        ></WhiteButton>
                      </div>
                    )}
                  <div className="quiz-exam-center-right-top">
                    {getCurrentQuestionData()?.type == "matching" ? (
                      <>
                        <h3 className="sub-title">
                          {"Question " +
                            (currentQuestion + 1) +
                            " - " +
                            getProgress()[1] +
                            " of " +
                            getProgress()[1]}
                          {viewingResults && getNIncorrectMatching() > 0 && (
                            <span
                              className="lost-pts"
                              style={{
                                marginLeft: "5px",
                                color: "#D83C3C",
                                fontSize: "18px",
                                fontWeight: 1000,
                              }}
                            >
                              {"-" + getNIncorrectMatching() + " points"}
                            </span>
                          )}
                        </h3>
                      </>
                    ) : (
                      <h3 className="sub-title">
                        {"Question " +
                          (currentQuestion + 1) +
                          " of " +
                          getProgress()[1]}
                      </h3>
                    )}

                    {getCurrentQuestionData()?.type != "matching" && ( // The question at the top of the screen. Matching does not include this question.
                      <div className="top-question-container">
                        <div className="title-container">
                          {setData?.terms[getCurrentQuestionData()?.term] !=
                            undefined &&
                            getCurrentQuestionData().type != "matching" &&
                            (getCurrentQuestionData()?.type == "t/f" ? ( // If its a true/false
                              <>
                                {(optionsResponse["answer_with"] == "0"
                                  ? setData?.terms[
                                      getCurrentQuestionData()?.term
                                    ][4]
                                  : setData?.terms[
                                      getCurrentQuestionData()?.term
                                    ][5]) != null ? ( // Equation exists
                                  <>
                                    {/* Render the equation (term) */}
                                    <StaticMathField>
                                      {optionsResponse["answer_with"] == "0"
                                        ? setData?.terms[
                                            getCurrentQuestionData()?.term
                                          ][0]
                                        : setData?.terms[
                                            getCurrentQuestionData()?.term
                                          ][1]}
                                    </StaticMathField>
                                    {/* Render the equation (definition) */}
                                    {(optionsResponse["answer_with"] == "0"
                                      ? setData?.terms[
                                          getCurrentQuestionData()?.term
                                        ][5]
                                      : setData?.terms[
                                          getCurrentQuestionData()?.term
                                        ][4]) != null ? (
                                      <StaticMathField>
                                        {" = " +
                                          (optionsResponse["answer_with"] == "0"
                                            ? setData?.terms[
                                                getCurrentQuestionData()
                                                  ?.response
                                              ][1]
                                            : setData?.terms[
                                                getCurrentQuestionData()
                                                  ?.response
                                              ][0])}
                                      </StaticMathField>
                                    ) : (
                                      <h3 key={currentQuestion}>
                                        {" - " +
                                          (optionsResponse["answer_with"] == "0"
                                            ? setData?.terms[
                                                getCurrentQuestionData()?.term
                                              ][1]
                                            : setData?.terms[
                                                getCurrentQuestionData()?.term
                                              ][0])}
                                      </h3>
                                    )}
                                  </>
                                ) : (optionsResponse["answer_with"] == "0"
                                    ? setData?.terms[
                                        getCurrentQuestionData()?.term
                                      ][5]
                                    : setData?.terms[
                                        getCurrentQuestionData()?.term
                                      ][4]) != null ? (
                                  <>
                                    <h3 key={currentQuestion}>
                                      {optionsResponse["answer_with"] == "0"
                                        ? setData?.terms[
                                            getCurrentQuestionData()?.term
                                          ][0]
                                        : setData?.terms[
                                            getCurrentQuestionData()?.term
                                          ][1]}
                                    </h3>
                                    <StaticMathField>
                                      {" = " +
                                        (optionsResponse["answer_with"] == "0"
                                          ? setData?.terms[
                                              getCurrentQuestionData()?.response
                                            ][1]
                                          : setData?.terms[
                                              getCurrentQuestionData()?.response
                                            ][0])}
                                    </StaticMathField>
                                  </>
                                ) : (
                                  <h3 key={currentQuestion}>
                                    {(optionsResponse["answer_with"] == "0"
                                      ? setData?.terms[
                                          getCurrentQuestionData()?.term
                                        ][0]
                                      : setData?.terms[
                                          getCurrentQuestionData()?.term
                                        ][1]) +
                                      " - " +
                                      (optionsResponse["answer_with"] == "0"
                                        ? setData?.terms[
                                            getCurrentQuestionData()?.response
                                          ][1]
                                        : setData?.terms[
                                            getCurrentQuestionData()?.response
                                          ][0])}
                                  </h3>
                                )}

                                {viewingResults &&
                                  getQuestionDataByIndex_Results(
                                    currentQuestion
                                  ) == "I" && (
                                    <span className="lost-pts">
                                      {"-1 point"}
                                    </span>
                                  )}
                              </>
                            ) : (optionsResponse["answer_with"] == "0"
                                ? setData?.terms[
                                    getCurrentQuestionData()?.term
                                  ][4]
                                : setData?.terms[
                                    getCurrentQuestionData()?.term
                                  ][5]) != null ? (
                              <>
                                <StaticMathField>
                                  {optionsResponse["answer_with"] == "0"
                                    ? setData?.terms[
                                        getCurrentQuestionData()?.term
                                      ][0]
                                    : setData?.terms[
                                        getCurrentQuestionData()?.term
                                      ][1]}
                                </StaticMathField>
                                {viewingResults &&
                                  getQuestionDataByIndex_Results(
                                    currentQuestion
                                  ) == "I" && (
                                    <span className="lost-pts">
                                      {"-1 point"}
                                    </span>
                                  )}
                              </>
                            ) : (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <h3 className="title">
                                  {" "}
                                  {/* Question */}
                                  {getCurrentQuestionData()?.type == "mc" &&
                                    (optionsResponse["answer_with"] == "0"
                                      ? setData?.terms[
                                          getCurrentQuestionData()?.term
                                        ][0]
                                      : setData?.terms[
                                          getCurrentQuestionData()?.term
                                        ][1])}
                                  {getCurrentQuestionData()?.type ==
                                    "short-answer" &&
                                    (optionsResponse["answer_with"] == "0"
                                      ? setData?.terms[
                                          getCurrentQuestionData()?.term
                                        ][0]
                                      : setData?.terms[
                                          getCurrentQuestionData()?.term
                                        ][1])}
                                  {getCurrentQuestionData()?.type == "t/f" &&
                                    setData?.terms[
                                      getCurrentQuestionData()?.response
                                    ] != undefined &&
                                    (optionsResponse["answer_with"] == "0"
                                      ? setData?.terms[
                                          getCurrentQuestionData()?.term
                                        ][0]
                                      : setData?.terms[
                                          getCurrentQuestionData()?.term
                                        ][1]) +
                                      " - " +
                                      (optionsResponse["answer_with"] == "0"
                                        ? setData?.terms[
                                            getCurrentQuestionData()?.response
                                          ][1]
                                        : setData?.terms[
                                            getCurrentQuestionData()?.response
                                          ][0])}
                                  {viewingResults &&
                                    getQuestionDataByIndex_Results(
                                      currentQuestion
                                    ) == "I" && (
                                      <span className="lost-pts">
                                        {"-1 point"}
                                      </span>
                                    )}
                                </h3>{" "}
                                {Object.keys(
                                  setData?.psd?.most_difficult_terms
                                )?.indexOf(
                                  getCurrentQuestionData()?.term.toString()
                                ) != -1 && (
                                  <Tooltip
                                    content={
                                      "This is one of your most difficult terms."
                                    }
                                    direction="top"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="37"
                                      height="37"
                                      viewBox="0 0 37 37"
                                      className="most-difficult-term-mark"
                                    >
                                      <g
                                        id="Group_2070"
                                        data-name="Group 2070"
                                        transform="translate(-679 -397)"
                                      >
                                        <circle
                                          id="Ellipse_489"
                                          data-name="Ellipse 489"
                                          cx="18.5"
                                          cy="18.5"
                                          r="18.5"
                                          transform="translate(679 397)"
                                          fill="#8358e8"
                                        />
                                        <path
                                          id="sentiment_stressed_FILL0_wght400_GRAD0_opsz48"
                                          d="M86.482,188.607l4.252-3.092-4.252-3.092-1.07,1.427,2.29,1.665-2.29,1.665Zm10.823,0,1.07-1.427-2.29-1.665,2.29-1.665-1.07-1.427-4.252,3.092Zm-7.2,5.293,1.784-1.784,1.784,1.784,1.784-1.784,1.16,1.16,1.249-1.249-2.408-2.408L93.678,191.4l-1.784-1.784L90.109,191.4l-1.784-1.784-2.408,2.408,1.249,1.249,1.16-1.16Zm1.784,5.887a11.581,11.581,0,0,1-4.638-.937,11.875,11.875,0,0,1-6.318-6.318,11.954,11.954,0,0,1,0-9.277,11.875,11.875,0,0,1,6.318-6.318,11.954,11.954,0,0,1,9.277,0,11.875,11.875,0,0,1,6.318,6.318,11.954,11.954,0,0,1,0,9.277,11.875,11.875,0,0,1-6.318,6.318A11.582,11.582,0,0,1,91.894,199.787ZM91.894,187.893Zm0,10.109a10.032,10.032,0,1,0-7.171-2.938A9.757,9.757,0,0,0,91.894,198Z"
                                          transform="translate(605.607 227.606)"
                                          fill="#fff"
                                        />
                                      </g>
                                    </svg>
                                  </Tooltip>
                                )}
                              </div>
                            ))}
                        </div>
                        <div className="title-images-container">
                          {
                            /* Images */

                            (optionsResponse["answer_with"] == "0"
                              ? setData?.terms[
                                  getCurrentQuestionData()?.term
                                ][2]
                              : setData?.terms[
                                  getCurrentQuestionData()?.term
                                ][3]) != undefined &&
                              optionsResponse["images"] == "1" && (
                                <img
                                  src={
                                    optionsResponse["answer_with"] == "0"
                                      ? setData?.terms[
                                          getCurrentQuestionData()?.term
                                        ][2]
                                      : setData?.terms[
                                          getCurrentQuestionData()?.term
                                        ][3]
                                  }
                                ></img>
                              )
                          }
                          {
                            // T/F Images
                            getCurrentQuestionData()?.type == "t/f" && (
                              <>
                                {(optionsResponse["answer_with"] == "0"
                                  ? setData?.terms[
                                      getCurrentQuestionData()?.term
                                    ][2]
                                  : setData?.terms[
                                      getCurrentQuestionData()?.term
                                    ][3]) != undefined &&
                                  optionsResponse["images"] == "1" && (
                                    <>
                                      <h1>-</h1>
                                      <img
                                        src={
                                          optionsResponse["answer_with"] == "0"
                                            ? setData?.terms[
                                                getCurrentQuestionData()?.term
                                              ][2]
                                            : setData?.terms[
                                                getCurrentQuestionData()?.term
                                              ][3]
                                        }
                                      ></img>
                                    </>
                                  )}
                              </>
                            )
                          }
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="quiz-exam-center-right-bottom">
                    {" "}
                    {/* Answer Choices */}
                    {getCurrentQuestionData()?.type == "mc" && (
                      <div>
                        {getCurrentQuestionData()?.options.map((v, c) => (
                          <>
                            {viewingResults &&
                            getQuestionDataByIndex_Results(currentQuestion) ==
                              "C" ? ( // Viewing results for multiple choice
                              <>
                                <div
                                  className={
                                    "answer-cont " +
                                    (getCurrentQuestionData()?.correct_choice ==
                                    v
                                      ? "viewing-results-correct"
                                      : "viewing-results-greyed")
                                  }
                                  onClick={() => {}}
                                >
                                  <div className="horizontal-div">
                                    <div className="answer-number">
                                      {alphabet[c]}
                                    </div>
                                    {setData?.terms[v] != undefined &&
                                    (optionsResponse["answer_with"] == "0"
                                      ? setData?.terms[v][5]
                                      : setData?.terms[v][4]) != null ? (
                                      <StaticMathField>
                                        {optionsResponse["answer_with"] == "0"
                                          ? setData?.terms[v][1]
                                          : setData?.terms[v][0]}
                                      </StaticMathField>
                                    ) : (
                                      <h4 className="answer">
                                        {optionsResponse["answer_with"] == "0"
                                          ? setData?.terms[v][1]
                                          : setData?.terms[v][0]}
                                      </h4>
                                    )}
                                  </div>
                                  {(optionsResponse["answer_with"] == "0"
                                    ? setData?.terms[v][3]
                                    : setData?.terms[v][2]) != null &&
                                    optionsResponse["images"] == "1" && (
                                      <img
                                        src={
                                          optionsResponse["answer_with"] == "0"
                                            ? setData?.terms[v][3]
                                            : setData?.terms[v][2]
                                        }
                                      ></img>
                                    )}
                                </div>
                              </>
                            ) : viewingResults &&
                              getQuestionDataByIndex_Results(currentQuestion) ==
                                "I" ? (
                              <>
                                <div
                                  className={
                                    "answer-cont " +
                                    (getCurrentQuestionData()?.correct_choice ==
                                      v ||
                                    getCurrentQuestionData()
                                      ?.correct_override == "C"
                                      ? "viewing-results-correct"
                                      : getCurrentQuestionData()?.answer == v
                                      ? "viewing-results-incorrect"
                                      : "viewing-results-greyed")
                                  }
                                  onClick={() => {}}
                                >
                                  <div className="horizontal-div">
                                    <div className="answer-number">
                                      {alphabet[c]}
                                    </div>
                                    {setData?.terms[v] != undefined &&
                                    (optionsResponse["answer_with"] == "0"
                                      ? setData?.terms[v][5]
                                      : setData?.terms[v][4]) != null ? (
                                      <StaticMathField>
                                        {optionsResponse["answer_with"] == "0"
                                          ? setData?.terms[v][1]
                                          : setData?.terms[v][0]}
                                      </StaticMathField>
                                    ) : (
                                      <h4 className="answer">
                                        {optionsResponse["answer_with"] == "0"
                                          ? setData?.terms[v][1]
                                          : setData?.terms[v][0]}
                                      </h4>
                                    )}
                                  </div>
                                  {(optionsResponse["answer_with"] == "0"
                                    ? setData?.terms[v][3]
                                    : setData?.terms[v][2]) != null &&
                                    optionsResponse["images"] == "1" && (
                                      <img
                                        src={
                                          optionsResponse["answer_with"] == "0"
                                            ? setData?.terms[v][3]
                                            : setData?.terms[v][2]
                                        }
                                      ></img>
                                    )}
                                </div>
                              </>
                            ) : (
                              // Normal testing
                              <>
                                <div
                                  className={
                                    "answer-cont " +
                                    (getCurrentQuestionData()?.answer == v ||
                                    getCurrentQuestionData()
                                      ?.correct_override == "C"
                                      ? "answer-cont-selected"
                                      : "")
                                  }
                                  onClick={() => {
                                    overrideCurrentQuestionAnswer(v);
                                  }}
                                >
                                  <div className="horizontal-div">
                                    <div className="answer-number">
                                      {alphabet[c]}
                                    </div>
                                    {setData?.terms[v] != undefined &&
                                    (optionsResponse["answer_with"] == "0"
                                      ? setData?.terms[v][5]
                                      : setData?.terms[v][4]) != null ? (
                                      <StaticMathField>
                                        {optionsResponse["answer_with"] == "0"
                                          ? setData?.terms[v][1]
                                          : setData?.terms[v][0]}
                                      </StaticMathField>
                                    ) : (
                                      <h4 className="answer">
                                        {optionsResponse["answer_with"] == "0"
                                          ? setData?.terms[v][1]
                                          : setData?.terms[v][0]}
                                      </h4>
                                    )}
                                  </div>
                                  {(optionsResponse["answer_with"] == "0"
                                    ? setData?.terms[v][3]
                                    : setData?.terms[v][2]) != null &&
                                    optionsResponse["images"] == "1" && (
                                      <img
                                        src={
                                          optionsResponse["answer_with"] == "0"
                                            ? setData?.terms[v][3]
                                            : setData?.terms[v][2]
                                        }
                                      ></img>
                                    )}
                                </div>
                              </>
                            )}
                          </>
                        ))}
                      </div>
                    )}
                    {viewingResults &&
                    getCurrentQuestionData()?.type == "t/f" ? (
                      <div>
                        {/* Correct */}
                        {getQuestionDataByIndex_Results(currentQuestion) ==
                          "C" &&
                          getCurrentQuestionData()?.correct_choice == "T" && (
                            <>
                              <div
                                className={
                                  "answer-cont viewing-results-correct"
                                }
                              >
                                <div className="horizontal-div">
                                  <div className="answer-number">{"T"}</div>
                                  <h4 className="answer">{"True"}</h4>
                                </div>
                              </div>
                              <div
                                className={"answer-cont viewing-results-greyed"}
                              >
                                <div className="horizontal-div">
                                  <div className="answer-number">{"F"}</div>
                                  <h4 className="answer">{"False"}</h4>
                                </div>
                              </div>
                            </>
                          )}

                        {getQuestionDataByIndex_Results(currentQuestion) ==
                          "C" &&
                          getCurrentQuestionData()?.correct_choice == "F" && (
                            <>
                              <div
                                className={"answer-cont viewing-results-greyed"}
                              >
                                <div className="horizontal-div">
                                  <div className="answer-number">{"T"}</div>
                                  <h4 className="answer">{"True"}</h4>
                                </div>
                              </div>
                              <div
                                className={
                                  "answer-cont viewing-results-correct"
                                }
                              >
                                <div className="horizontal-div">
                                  <div className="answer-number">{"F"}</div>
                                  <h4 className="answer">{"False"}</h4>
                                </div>
                              </div>
                            </>
                          )}

                        {/* Incorrect */}

                        {getQuestionDataByIndex_Results(currentQuestion) ==
                          "I" &&
                          getCurrentQuestionData()?.correct_choice == "T" && (
                            <>
                              <div
                                className={
                                  "answer-cont viewing-results-correct"
                                }
                              >
                                <div className="horizontal-div">
                                  <div className="answer-number">{"T"}</div>
                                  <h4 className="answer">{"True"}</h4>
                                </div>
                              </div>
                              <div
                                className={
                                  "answer-cont viewing-results-incorrect"
                                }
                              >
                                <div className="horizontal-div">
                                  <div className="answer-number">{"F"}</div>
                                  <h4 className="answer">{"False"}</h4>
                                </div>
                              </div>
                            </>
                          )}

                        {getQuestionDataByIndex_Results(currentQuestion) ==
                          "I" &&
                          getCurrentQuestionData()?.correct_choice == "F" && (
                            <>
                              <div
                                className={
                                  "answer-cont viewing-results-incorrect"
                                }
                              >
                                <div className="horizontal-div">
                                  <div className="answer-number">{"T"}</div>
                                  <h4 className="answer">{"True"}</h4>
                                </div>
                              </div>
                              <div
                                className={
                                  "answer-cont viewing-results-correct"
                                }
                              >
                                <div className="horizontal-div">
                                  <div className="answer-number">{"F"}</div>
                                  <h4 className="answer">{"False"}</h4>
                                </div>
                              </div>
                            </>
                          )}
                      </div>
                    ) : (
                      getCurrentQuestionData()?.type == "t/f" && (
                        <div>
                          <div
                            className={
                              "answer-cont " +
                              (getCurrentQuestionData()?.answer == "T" ||
                              getCurrentQuestionData()?.correct_override == "C"
                                ? "answer-cont-selected"
                                : "")
                            }
                            onClick={() => {
                              overrideCurrentQuestionAnswer("T");
                            }}
                          >
                            <div className="horizontal-div">
                              <div className="answer-number">{"T"}</div>
                              <h4 className="answer">{"True"}</h4>
                            </div>
                          </div>
                          <div
                            className={
                              "answer-cont " +
                              (getCurrentQuestionData()?.answer == "F" ||
                              getCurrentQuestionData()?.correct_override == "C"
                                ? "answer-cont-selected"
                                : "")
                            }
                            onClick={() => {
                              overrideCurrentQuestionAnswer("F");
                            }}
                          >
                            <div className="horizontal-div">
                              <div className="answer-number">{"F"}</div>
                              <h4 className="answer">{"False"}</h4>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                    {getCurrentQuestionData().type == "short-answer" && (
                      <>
                        <div
                          className={
                            "textarea-wrapper textarea-wrapper-orig " +
                            (viewingResults &&
                            getQuestionDataByIndex_Results(currentQuestion) !=
                              "C"
                              ? "textarea-wrapper-incorrect"
                              : "")
                          }
                          style={
                            (optionsResponse["answer_with"] == "0"
                              ? setData?.terms[
                                  getCurrentQuestionData()?.term
                                ][5]
                              : setData?.terms[
                                  getCurrentQuestionData()?.term
                                ][4]) != null && viewingResults
                              ? { justifyContent: "flex-start" }
                              : {}
                          }
                        >
                          {(optionsResponse["answer_with"] == "0"
                            ? setData?.terms[getCurrentQuestionData()?.term][5]
                            : setData?.terms[
                                getCurrentQuestionData()?.term
                              ][4]) != null && !viewingResults ? ( // editing your math equation to give an answer
                            <EditableMathField
                              config={{
                                sumStartsWithNEquals: true,
                                autoCommands:
                                  "bar overline sqrt sum prod int alpha beta gamma delta epsilon zeta eta theta iota kappa lambda mu nu xi omikron pi rho sigma tau upsilon phi chi psi omega rangle langle otimes neq leq ll geq gg approx dagger angle and or infty",
                              }}
                              latex={
                                getCurrentQuestionData().answer
                                  ? getCurrentQuestionData().answer
                                  : ""
                              }
                              onChange={(mathField) => {
                                overrideCurrentQuestionAnswer(
                                  mathField.latex()
                                );
                              }}
                            />
                          ) : (optionsResponse["answer_with"] == "0"
                              ? setData?.terms[
                                  getCurrentQuestionData()?.term
                                ][5]
                              : setData?.terms[
                                  getCurrentQuestionData()?.term
                                ][4]) != null && viewingResults ? ( // viewing your math equation results
                            <StaticMathField>
                              {getCurrentQuestionData().answer}
                            </StaticMathField>
                          ) : (
                            // non math equation
                            <>
                              {" "}
                              <textarea
                                disabled={viewingResults}
                                value={getCurrentQuestionData().answer}
                                key={currentQuestion}
                                style={
                                  viewingResults &&
                                  getCurrentQuestionData()?.answer !=
                                    getCurrentQuestionData()?.correct_choice &&
                                  getCurrentQuestionData()?.correct_override !=
                                    "C"
                                    ? {
                                        textDecoration: "line-through",
                                        border: "3px solid #D83C3C",
                                        borderRadius: "10px",
                                        color: "#D83C3C",
                                      }
                                    : viewingResults
                                    ? {
                                        color: "#58E892",
                                        border: "3px solid #58E892",
                                        borderRadius: "10px",
                                      }
                                    : {}
                                }
                                onChange={(e) => {
                                  if (e.target.value.length == 0) {
                                    overrideCurrentQuestionAnswer(null);
                                  }
                                  overrideCurrentQuestionAnswer(e.target.value);
                                  // handleChange(e);
                                }}
                                placeholder={
                                  (optionsResponse["answer_with"] == "0"
                                    ? setData?.terms[
                                        getCurrentQuestionData()?.term
                                      ][5]
                                    : setData?.terms[
                                        getCurrentQuestionData()?.term
                                      ][4]) != null
                                    ? "Define the term.."
                                    : optionsResponse["answer_with"] == "0"
                                    ? "Define '" +
                                      (optionsResponse["answer_with"] == "0"
                                        ? setData?.terms[
                                            getCurrentQuestionData()?.term
                                          ][0]
                                        : setData?.terms[
                                            getCurrentQuestionData()?.term
                                          ][1]) +
                                      "'"
                                    : "Enter the term for this definition."
                                }
                              ></textarea>
                            </>
                          )}
                        </div>
                        {viewingResults &&
                          getCurrentQuestionData()?.answer !=
                            getCurrentQuestionData()?.correct_choice &&
                          getCurrentQuestionData()?.correct_override !=
                            "C" /* You got it wrong */ &&
                          (optionsResponse["answer_with"] == "0"
                            ? setData?.terms[getCurrentQuestionData()?.term][5]
                            : setData?.terms[
                                getCurrentQuestionData()?.term
                              ][4]) == null && (
                            <div className="textarea-wrapper">
                              <textarea
                                disabled={true}
                                value={getCurrentQuestionData()?.correct_choice}
                                style={{
                                  color: "#58E892",
                                  border: "3px solid #58E892",
                                  borderRadius: "10px",
                                }}
                              ></textarea>
                            </div>
                          )}
                        {getCurrentQuestionData()?.answer !=
                          getCurrentQuestionData()?.correct_choice &&
                          getCurrentQuestionData()?.correct_override == null &&
                          getCurrentQuestionData()?.answer != null &&
                          viewingResults && (
                            <p
                              className="saq-override"
                              onClick={() => {
                                override_saq(
                                  setid,
                                  id,
                                  getCurrentQuestionIndex(),
                                  (data) => {
                                    setQuizQueue(data.data);
                                    get_quiz_details(setid, (data) => {
                                      setQuizDetails(data.data);
                                    });
                                    fetch_quiz_info(setid, id, (data) => {
                                      setResultsInfo(data);
                                    });
                                  }
                                );
                              }}
                            >
                              Override: I was right.
                            </p>
                          )}
                        {getCurrentQuestionData()?.correct_override != null &&
                          viewingResults && (
                            <p
                              className="saq-override"
                              style={{ cursor: "default" }}
                            >
                              Note: This question was overwritten.
                            </p>
                          )}
                        {viewingResults &&
                          getQuestionDataByIndex_Results(currentQuestion) !=
                            "C" &&
                          (optionsResponse["answer_with"] == "0"
                            ? setData?.terms[getCurrentQuestionData()?.term][5]
                            : setData?.terms[
                                getCurrentQuestionData()?.term
                              ][4]) != null && (
                            <div
                              className="textarea-wrapper textarea-wrapper-correct"
                              style={{ justifyContent: "flex-start" }}
                            >
                              <StaticMathField>
                                {getCurrentQuestionData()?.correct_choice}
                              </StaticMathField>
                            </div>
                          )}
                      </>
                    )}
                    {getCurrentQuestionData().type == "matching" && (
                      <>
                        <div className="matching-answer-container">
                          <div className="matching-answer-container-left">
                            {" "}
                            {/* Left choices */}
                            {getCurrentQuestionData().options[0].map(
                              (
                                v,
                                c // v = index of term, c = count
                              ) => (
                                <div
                                  className={
                                    (viewingResults
                                      ? resultsInfo?.grade[3]?.[3]?.[0]?.[v] ===
                                        "C"
                                        ? "viewing-results viewing-results-correct"
                                        : "viewing-results viewing-results-incorrect"
                                      : "") + " option"
                                  }
                                  style={
                                    selectedMatchOption == v
                                      ? {
                                          backgroundColor: getColor(v),
                                          color: "white",
                                          transform: "scale(1.1)",
                                        }
                                      : getCurrentQuestionData().answer[v] !=
                                        undefined
                                      ? {
                                          backgroundColor: getColor(v),
                                          color: "white",
                                        }
                                      : {}
                                  }
                                  id={"matcha" + v}
                                  onClick={() => {
                                    if (!viewingResults) {
                                      setSelectedMatchOption(v);
                                      setMatchChoice1(v);
                                    }
                                  }}
                                >
                                  <div className="option-text">
                                    <div
                                      className="option-number"
                                      style={
                                        selectedMatchOption == v ||
                                        getCurrentQuestionData().answer[v] !=
                                          undefined
                                          ? { color: "white" }
                                          : {}
                                      }
                                    >
                                      {alphabet[c]}
                                    </div>
                                    <div className="answer-number"></div>
                                    {(optionsResponse["answer_with"] == "0"
                                      ? setData?.terms[v][4]
                                      : setData?.terms[v][5]) != null ? (
                                      <StaticMathField>
                                        {optionsResponse["answer_with"] == "0"
                                          ? setData?.terms[v][0]
                                          : setData?.terms[v][1]}
                                      </StaticMathField>
                                    ) : (
                                      <h4
                                        className="option-title"
                                        style={
                                          selectedMatchOption == v ||
                                          getCurrentQuestionData().answer[v] !=
                                            undefined
                                            ? { color: "white" }
                                            : {}
                                        }
                                      >
                                        {optionsResponse["answer_with"] == "0"
                                          ? setData?.terms[v][0]
                                          : setData?.terms[v][1]}
                                      </h4>
                                    )}
                                    {setData?.psd.most_difficult_terms[v] !=
                                      null && (
                                      <Tooltip
                                        content={
                                          "This is one of your most difficult terms."
                                        }
                                        direction="top"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="37"
                                          height="37"
                                          viewBox="0 0 37 37"
                                          className="most-difficult-term-mark"
                                        >
                                          <g
                                            id="Group_2070"
                                            data-name="Group 2070"
                                            transform="translate(-679 -397)"
                                          >
                                            <circle
                                              id="Ellipse_489"
                                              data-name="Ellipse 489"
                                              cx="18.5"
                                              cy="18.5"
                                              r="18.5"
                                              transform="translate(679 397)"
                                              fill="#8358e8"
                                            />
                                            <path
                                              id="sentiment_stressed_FILL0_wght400_GRAD0_opsz48"
                                              d="M86.482,188.607l4.252-3.092-4.252-3.092-1.07,1.427,2.29,1.665-2.29,1.665Zm10.823,0,1.07-1.427-2.29-1.665,2.29-1.665-1.07-1.427-4.252,3.092Zm-7.2,5.293,1.784-1.784,1.784,1.784,1.784-1.784,1.16,1.16,1.249-1.249-2.408-2.408L93.678,191.4l-1.784-1.784L90.109,191.4l-1.784-1.784-2.408,2.408,1.249,1.249,1.16-1.16Zm1.784,5.887a11.581,11.581,0,0,1-4.638-.937,11.875,11.875,0,0,1-6.318-6.318,11.954,11.954,0,0,1,0-9.277,11.875,11.875,0,0,1,6.318-6.318,11.954,11.954,0,0,1,9.277,0,11.875,11.875,0,0,1,6.318,6.318,11.954,11.954,0,0,1,0,9.277,11.875,11.875,0,0,1-6.318,6.318A11.582,11.582,0,0,1,91.894,199.787ZM91.894,187.893Zm0,10.109a10.032,10.032,0,1,0-7.171-2.938A9.757,9.757,0,0,0,91.894,198Z"
                                              transform="translate(605.607 227.606)"
                                              fill="#fff"
                                            />
                                          </g>
                                        </svg>
                                      </Tooltip>
                                    )}
                                  </div>

                                  {(optionsResponse["answer_with"] == "0"
                                    ? setData?.terms[v][2]
                                    : setData?.terms[v][3]) != null &&
                                    optionsResponse["images"] == "1" && (
                                      <div className="option-image">
                                        <img
                                          src={
                                            optionsResponse["answer_with"] ==
                                            "0"
                                              ? setData?.terms[v][2]
                                              : setData?.terms[v][1]
                                          }
                                        ></img>
                                      </div>
                                    )}
                                </div>
                              )
                            )}
                          </div>
                          {/* Right choices */}
                          <div className="matching-answer-container-right">
                            {getCurrentQuestionData().options[1].map((v, c) => (
                              <div
                                className={
                                  (viewingResults
                                    ? resultsInfo?.grade[3]?.[3]?.[0]?.[v] ===
                                      "C"
                                      ? "viewing-results viewing-results-correct"
                                      : "viewing-results viewing-results-incorrect"
                                    : "") + " option"
                                }
                                style={
                                  getOriginalMatcher(v) != undefined
                                    ? {
                                        backgroundColor: getColor(
                                          getOriginalMatcher(v)
                                        ),
                                        color: "white",
                                      }
                                    : {}
                                }
                                id={"matchb" + v}
                                onClick={() => {
                                  if (
                                    !viewingResults &&
                                    matchChoice1 != undefined
                                  ) {
                                    var ans = {
                                      ...getCurrentQuestionData().answer,
                                    };
                                    ans[matchChoice1] = v;
                                    var valid = isValidMatchChoice(v, ans);
                                    var new_ans = { ...ans };
                                    if (!valid) {
                                      // responsible for undoing match collisions
                                      Object.keys(ans).forEach(function (c) {
                                        var val = ans[c];
                                        if (c != matchChoice1 && v == val) {
                                          new_ans[c] = null;
                                        }
                                      });
                                    }

                                    overrideCurrentQuestionAnswer(new_ans);
                                    updateMatchDataForCurrentQuestion();
                                  }
                                }}
                              >
                                <div
                                  style={
                                    !viewingResults &&
                                    getOriginalMatcher(v) != undefined
                                      ? {
                                          backgroundColor: getColor(
                                            getOriginalMatcher(v)
                                          ),
                                          color: "white",
                                        }
                                      : {}
                                  }
                                  className="option-number"
                                >
                                  {alphabet[c]}
                                </div>
                                <>
                                  {(optionsResponse["answer_with"] == "0"
                                    ? setData?.terms[v][5]
                                    : setData?.terms[v][4]) != null ? (
                                    <StaticMathField>
                                      {optionsResponse["answer_with"] == "0"
                                        ? setData?.terms[v][1]
                                        : setData?.terms[v][0]}
                                    </StaticMathField>
                                  ) : (
                                    <h4
                                      style={
                                        !viewingResults &&
                                        getOriginalMatcher(v) != undefined
                                          ? {
                                              backgroundColor: getColor(
                                                getOriginalMatcher(v)
                                              ),
                                              color: "white",
                                            }
                                          : {}
                                      }
                                      className="option-title"
                                    >
                                      {optionsResponse["answer_with"] == "0"
                                        ? setData?.terms[v][1]
                                        : setData?.terms[v][0]}
                                    </h4>
                                  )}
                                </>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    <div className="btn-container">
                      {" "}
                      {currentQuestion - 1 > -1 && (
                        <button
                          className="prev-btn"
                          onClick={() => {
                            if (currentQuestion - 1 < getProgress()[1]) {
                              setCurrentQuestion(currentQuestion - 1);
                            }
                          }}
                        >
                          <div className="arrow-icon-cont">
                            <BsArrowLeftShort className="arrow-icon" />
                          </div>{" "}
                          Previous
                        </button>
                      )}
                      {!viewingResults && getProgress()[0] == 100 ? (
                        <button
                          className="next-btn submit-btn"
                          onClick={() => {
                            setSubmitQuizPopup(true);
                          }}
                        >
                          Submit
                          <div className="arrow-icon-cont">
                            <BsCheck className="arrow-icon" />
                          </div>
                        </button>
                      ) : viewingResults &&
                        (quizQueue[3].length != 0
                          ? currentQuestion + 1 == arrayOfQuestions().length + 1
                          : currentQuestion + 1 ==
                            arrayOfQuestions().length) ? (
                        <button
                          className="next-btn"
                          onClick={() => {
                            setIsOnMainViewingResultsPage(true);
                          }}
                        >
                          Results
                          <BsInfoCircle className="info-icon" />
                        </button>
                      ) : (
                        getCurrentQuestionData().type != "matching" && (
                          <button
                            className="next-btn"
                            onClick={() => {
                              if (currentQuestion + 1 < getProgress()[1]) {
                                setCurrentQuestion(currentQuestion + 1);
                              }
                            }}
                          >
                            Next
                            <div className="arrow-icon-cont">
                              <BsArrowRightShort className="arrow-icon" />
                            </div>
                          </button>
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}
            {isOnMainViewingResultsPage && !isViewingDetails ? (
              <>
                <div className="quiz-finished-center-center">
                  {/* Your score page */}
                  <h2>Your score</h2>
                  <div className="chart-container">
                    <DoughnutChart config={doughnutChartConfig} />
                  </div>
                  <h2 className="">
                    You got {resultsInfo?.grade[0]} out of{" "}
                    {resultsInfo?.grade[4]} questions correct.
                  </h2>
                  {resultsInfo?.grade[1] === 100 && (
                    <p>You got a perfect score on this quiz! Wonderful job!</p>
                  )}
                  {resultsInfo?.grade[1] > 80 &&
                    resultsInfo?.grade[1] < 100 && (
                      <p>
                        Amazing! You almost got all of the questions correct!
                      </p>
                    )}
                  {resultsInfo?.grade[1] > 60 && resultsInfo?.grade[1] < 80 && (
                    <p>
                      Nice work! A bit more practice, and you'll get that A!
                    </p>
                  )}
                  {resultsInfo?.grade[1] < 60 && (
                    <p>
                      Continue reviewing your Flashcards to improve your score!
                    </p>
                  )}

                  <div className="btn-container">
                    <button
                      className="review-btn"
                      onClick={() => {
                        setCurrentQuestion(0);
                        setIsOnMainViewingResultsPage(false);
                      }}
                    >
                      Review
                      <div className="exclamation-icon-cont">
                        <BsInfoCircle className="exclamation-icon" />
                      </div>
                    </button>
                    <button
                      className="details-btn"
                      onClick={() => {
                        setIsViewingDetails(true);
                        get_quiz_details(setid, (data) => {
                          setQuizDetails(data.data);
                        });
                      }}
                    >
                      Details
                      <div className="arrow-icon-cont">
                        <BsArrowRightShort className="arrow-icon" />
                      </div>
                    </button>
                  </div>
                </div>
                <div className="other-quizzes">
                  <p>Other Quizzes</p>
                  <div className="other-quizzes-main">
                    {resultsInfo?.past_quizzes.map((data, index) => (
                      <div
                        className="other-quiz-container"
                        onClick={() => {
                          if (data[3]) {
                            window.location.href = "../" + data[0] + "/results";
                          } else {
                            ReactGA.event({
                              category: "User",
                              action: "Clicked Upgrade",
                              label: "Clicked from Other Quizzes",
                            });
                            window.location.href = "/upgrade";
                          }

                          //window.location.reload();
                        }}
                      >
                        {data[3] && <p>{"Quiz #" + (index + 1)}</p>}

                        <div className="doughnut-cont">
                          {data[3] ? (
                            <DoughnutChart
                              config={{
                                labels: [
                                  data[1] + "% correct",
                                  100 - data[1] + "% incorrect",
                                ],
                                ignoreTraditionalLabelCallback: true,
                                ignoreLegend: true,
                                //label: resultsInfo?.grade[1] + "%",
                                data: [data[1], 100 - data[1]],
                                bgColor: [getGradeColor(data[1]), "#363636"],
                                textColor: "#71EAA2",
                                textInside: data[2],
                                minFontSize: isMobile() ? 14 : 20,
                                maxFontSize: isMobile() ? 18 : 22,
                                rotate: 0,
                                divider: 2.6,
                              }}
                            ></DoughnutChart>
                          ) : (
                            <>
                              <p className="upgrade-to-view">
                                Upgrade to Quizzy+ to view this Quiz
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              isViewingDetails &&
              quizDetails != undefined && (
                <>
                  <div className="quiz-details">
                    <h1>Your quiz progress</h1>
                    <div className="progress-circles">
                      {quizDetails[0].map((data, c) => (
                        <div className="circle-container">
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <div
                              className={"circle"}
                              id={"circle-" + c}
                              style={{
                                backgroundColor:
                                  c == quizDetails[0].length - 1
                                    ? "white"
                                    : getGradeColor(data[0]),
                                border:
                                  c == quizDetails[0].length - 1 &&
                                  "4px solid " + getGradeColor(data[0]),
                              }}
                              onClick={() => {
                                window.location.href =
                                  "../" + data[3] + "/results";
                              }}
                            >
                              <p
                                style={{
                                  color:
                                    c == quizDetails[0].length - 1 &&
                                    getGradeColor(data[0]),
                                }}
                              >
                                {data[1]}
                              </p>
                            </div>
                            {c != quizDetails[0].length - 1 &&
                              !(
                                "ontouchstart" in window ||
                                navigator.maxTouchPoints > 0 ||
                                navigator.msMaxTouchPoints > 0
                              ) &&
                              quizDetails[0].length <
                                QUIZ_RESULTS_MAXIMUM_XARROW_RESULTS && (
                                <Xarrow
                                  start={"circle-" + c} //can be react ref
                                  end={"circle-" + (c + 1)} //or an id
                                  showHead={false}
                                  animateDrawing={true}
                                  color={getGradeColor(data[0])}
                                  path={"straight"}
                                />
                              )}

                            <p
                              style={{
                                color: getGradeColor(data[0]),
                              }}
                            >
                              {"Attempt #" + (c + 1)}
                            </p>
                          </div>
                        </div>
                      ))}
                      {user.membership < 3 && (
                        <div className="circle-container">
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <div
                              className={"circle"}
                              id={"upgrade-circle"}
                              style={{
                                backgroundColor: "#8358e8",
                              }}
                              onClick={() => {
                                window.location.href = "/upgrade";
                              }}
                            >
                              <p
                                style={{
                                  color: "white",
                                }}
                              >
                                {"?"}
                              </p>
                            </div>

                            <Xarrow
                              start={
                                "circle-" +
                                (Object.keys(quizDetails[0]).length - 1)
                              } //can be react ref
                              end={"upgrade-circle"} //or an id
                              showHead={false}
                              animateDrawing={true}
                              color={"#8358e8"}
                              path={"straight"}
                            />

                            <p
                              style={{
                                color: "#8358e8",
                              }}
                            >
                              {"Upgrade for more"}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="recall-chart">
                      <SteppedLineChart
                        className="graph-chrt"
                        config={quizProgress}
                        height={400}
                        width={200}
                      />
                    </div>
                    <h1>Your quiz time progress</h1>
                    <div className="recall-chart">
                      <SteppedLineChart
                        config={timeProgress}
                        height={400}
                        width={200}
                      />
                    </div>
                    <div className="most-missed">
                      <div className="upgrade-needed-title">
                        <h1>Most Difficult Terms</h1>
                        <PlusIcon big={true} />
                      </div>{" "}
                      {user.membership > 2 ? (
                        <MostDifficultTerms></MostDifficultTerms>
                      ) : (
                        <SampleData>
                          <MostDifficultTerms></MostDifficultTerms>
                        </SampleData>
                      )}
                    </div>
                    <div
                      className="confused-terms-container"
                      style={!user?.plans?.analytics ? { width: "80%" } : {}}
                    >
                      <div className="upgrade-needed-title">
                        <h1>Confused Terms</h1>
                        <AnalyticsBadge></AnalyticsBadge>
                      </div>
                      {user?.plans?.analytics ? (
                        <ConfusedTerms></ConfusedTerms>
                      ) : (
                        <SampleData>
                          <ConfusedTerms></ConfusedTerms>
                        </SampleData>
                      )}

                      {user?.plans?.analytics &&
                        Object.keys(quizDetails[4]).length == 0 && (
                          <p
                            className="label"
                            style={{
                              marginTop: "20px",
                              textAlign: "center",
                            }}
                          >
                            Hmm, there's nothing to display here, which means
                            you must really know your material!
                          </p>
                        )}
                      <br></br>
                      <br></br>
                      <br></br>
                      <br></br>
                      {/*<p className="label" style={{ fontStyle: "italics" }}>
                        Note: If this set is edited, all of your Quiz progress
                        will be removed to keep progress up to date.
                          </p>*/}
                    </div>
                  </div>
                </>
              )
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default QuizExam;
